import {
  ChevronDown,
  ChevronUp,
  Columns3,
  DoorOpen,
  Link2,
  Link2Off,
  Plus,
  RectangleHorizontal,
  RotateCcw,
  Trash2,
  Info,
} from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  summarizeGeometry,
  type AreaSource,
  type RoomGeometry,
} from '@obraplan/core/domain/takeoff/geometry';
import {
  emptyCatalog,
  guessMeasureBy,
  type ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import { guessAppliesTo } from '@obraplan/core/domain/takeoff/applies-to';
import { validationError } from '@obraplan/core/application/errors/app-error';
import { ROOM_KIND_LABEL, type Room, type RoomKind } from '@obraplan/core/domain/takeoff/room';
import type { UnitTypeKind } from '@obraplan/core/domain/takeoff/unit-type';
import {
  FLOOR_PLAN_KIND_LABEL,
  FLOOR_PLAN_KIND_ORDER,
  type FloorPlanKind,
} from '@obraplan/core/domain/takeoff/floor-plan';
import type { ProjectStructureView } from '@obraplan/core/application/use-cases/structure/get-project-structure';
import type { ProjectTakeoff } from '@obraplan/core/application/use-cases/takeoff/takeoff-use-cases';
import {
  GEOMETRY_CONFLICT,
  type ProjectGeometry,
} from '@obraplan/core/application/use-cases/takeoff/geometry-use-cases';
import {
  OPENING_KIND_LABEL,
  wallGrossArea,
  type OpeningKind,
  type Wall,
} from '@obraplan/core/domain/takeoff/wall';
import {
  describeSection,
  elementEspala,
  elementRun,
  segmentAdvance,
  segmentFaceArea,
  segmentShadowArea,
  thicknessAcrossWall,
  ELEMENT_ORIENTATIONS,
  orientationLabel,
  STRUCTURAL_ELEMENT_KIND_LABEL,
  type AdvanceSide,
  type ElementOrientation,
  type ElementSegment,
  type StructuralElement,
  type StructuralElementKind,
} from '@obraplan/core/domain/takeoff/structural-element';
import { polygonArea } from '@obraplan/core/domain/takeoff/polygon';
import { SHAFT_LABEL } from '@obraplan/core/domain/takeoff/shaft';
import { pecasImportadas } from '@obraplan/core/domain/takeoff/planilha-por-peca';
import type { ProjectId, RoomId, UnitOfMeasureId, WallId } from '@obraplan/core/domain/shared/ids';
import { err, ok } from '@obraplan/core/domain/shared/result';
import { lockedServicesMessage } from '@obraplan/core/domain/takeoff/project-budget';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Input, Label } from '@/components/ui/input';
import { TabPanel, type TabItem } from '@/components/ui/tabs';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { ConfirmDialog } from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { PlanTracer } from '@/features/takeoff/PlanTracer';
import {
  andaresSemDesenho,
  pavimentosSemDesenhoDaObra,
} from '@/features/takeoff/estacas-da-prancha';
import { ServiceSheetImport } from '@/features/takeoff/ServiceSheetImport';
import { servicosDaObra, servicosDesativadosDaObra } from '@/features/takeoff/modelo-de-indices';
import { EscolherServicos } from '@/features/takeoff/EscolherServicos';
import { aprovacaoDe, useServicosAprovados } from '@/features/takeoff/servicos-aprovados';
import {
  fatoresDoTipoNosComodos,
  trazerAMediaDoApartamento,
} from '@/features/takeoff/trazer-a-media-do-apartamento';
import { useMediaDoApartamento } from '@/features/takeoff/use-media-do-apartamento';
import { primeirasMudancas, type MudancaNoAprovado } from '@/features/takeoff/desenho-travado';
import { FaixaDoQuantitativo } from '@/features/takeoff/FaixaDoQuantitativo';
import { MarcarDaPlanilha } from '@/features/takeoff/MarcarDaPlanilha';
import {
  montarEscopoDaObra,
  ofertaDoPainel,
  recortarTipologia,
  servicosDaAba,
  type FiltroDoQuantitativo,
  type ServicoDaAba,
} from '@/features/takeoff/escopo-da-tipologia';
import { ComoFunciona } from '@/features/takeoff/ComoFunciona';
import { ItensEConsolidacao } from '@/features/takeoff/ItensEConsolidacao';
import type { PavimentoDaObra } from '@/features/takeoff/destino-por-peca';
import { funcoesDaObra, type ItensParaGravar } from '@/features/takeoff/do-servidor';
import { OrcamentoCard } from '@/features/takeoff/Orcamento';
import { OrcamentoDaObra } from '@/features/takeoff/OrcamentoDaObra';
import { naOrdemDaObra } from '@/features/takeoff/orcamento-da-obra';
import { NomeInput, NumeroInput } from '@/features/takeoff/campos';
import { fatoresDoComodo } from '@/features/takeoff/fatores-proprios';
import { FatoresProprios } from '@/features/takeoff/FatoresProprios';
import { PavimentoBloco, type AcoesDaEspinha } from '@/features/takeoff/PavimentoBloco';
import {
  criarDesenhoComAreaUnica,
  espinhaNomes,
  nomeDaAreaUnica,
} from '@/features/takeoff/area-unica';
import { metros } from '@/features/takeoff/formato';
import {
  useWorkspaceDraft,
  type DisciplinasParaGravar,
  type ResultadoDaGravacaoDoDesenho,
} from '@/features/takeoff/use-workspace-draft';
import { usePranchaDoPavimento } from '@/features/takeoff/use-prancha-do-pavimento';
import { geometriaDaTipologia } from '@/features/takeoff/medido-da-tipologia';
import { useContainer, useSessionState, useVisibleTabs } from '@/features/session/use-session';
import { usePecasDaPlanilha } from '@/features/takeoff/use-pecas-da-planilha';
import { useFotoDaMedida } from '@/features/takeoff/use-foto-da-medida';
import type { FotoDaMedida } from '@/features/takeoff/foto-da-medida';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';
import {
  activeTypology,
  DEFAULT_CEILING_HEIGHT,
  DEFAULT_WALL_HEIGHT,
  floorsOf,
  floorsOfDrawing,
  hasStructure,
  describeWall,
  groupWallsByRoom,
  pairableAcrossTypologies,
  pairableWith,
  partyWallsTouching,
  structureOf,
  wallLabelOf,
  wallShortName,
  type CandidataDeFora,
  type TypologyDraft,
  type WorkspaceAction,
  type WorkspaceDraft,
} from '@/features/takeoff/typology-draft';

/**
 * Montagem da tipologia — o protótipo de uso da Fase 14.
 *
 * A tela existe **antes** da persistência de propósito: o risco da fase não é guardar, é
 * montar. Se casar vinte paredes na mão for penoso, nenhuma tabela conserta — e é melhor
 * descobrir isso com o engenheiro numa tarde do que depois de escrever migração.
 *
 * Por isso o rascunho fica no navegador, com o aviso à vista. Nada aqui atravessa para a
 * obra.
 */

const SELECT_CLASS =
  'h-11 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-2 text-sm md:h-9';

const ROOM_KINDS = Object.keys(ROOM_KIND_LABEL) as readonly RoomKind[];

/**
 * De onde veio a área, dito na tela.
 *
 * Sem isso, o número calculado das paredes e o número que sobrou de largura × comprimento
 * ficariam idênticos — e só o primeiro acompanha quando alguém corrige uma parede.
 */
const ORIGEM_DA_AREA: Readonly<Record<AreaSource, string>> = {
  CONTORNO: 'do contorno da planta',
  PAREDES: 'das paredes',
  MEDIDAS: 'de largura × comprimento',
  CORRIGIDA: 'corrigida à mão',
};

/** O catálogo de quem ainda não tem nenhum — um só, para a memória do recorte não se refazer à toa. */
const CATALOGO_VAZIO: ServiceCatalog = emptyCatalog();

/** As partes do quantitativo, na mesma tela. */
type Aba = 'MONTAGEM' | 'ITENS' | 'ORCAMENTO';

const ABAS: readonly TabItem<Aba>[] = [
  { id: 'MONTAGEM', label: 'Montagem' },
  { id: 'ITENS', label: 'Itens e consolidação' },
  { id: 'ORCAMENTO', label: 'Orçamento' },
];

/** A rota de cada aba — a URL é quem guarda em qual delas a pessoa está. */
const CAMINHO_DA_ABA: Readonly<Record<Aba, string>> = {
  MONTAGEM: '/quantitativo',
  ITENS: '/quantitativo/itens',
  ORCAMENTO: '/quantitativo/orcamento',
};

/** Qual aba o endereço abre: os caminhos das abas não são prefixo um do outro, só da Montagem. */
function abaDoEndereco(caminho: string): Aba {
  if (caminho.startsWith(CAMINHO_DA_ABA.ORCAMENTO)) return 'ORCAMENTO';
  if (caminho.startsWith(CAMINHO_DA_ABA.ITENS)) return 'ITENS';
  return 'MONTAGEM';
}

/**
 * O quantitativo inteiro, com **um** rascunho.
 *
 * As duas metades eram duas rotas, e trocar de rota no meio do trabalho era o atrito: o
 * serviço se define de um lado e os itens dele do outro. Viraram abas — e a mudança que
 * importa não é visual, é onde o estado mora: o `useWorkspaceDraft` é chamado **aqui, uma
 * vez**, e as duas abas recebem o mesmo `workspace` e o mesmo `dispatch`.
 *
 * Dois `useWorkspaceDraft` vivos ao mesmo tempo teriam duas cópias do rascunho, cada uma
 * gravando por cima da outra no `localStorage` — o próprio módulo avisa disso. Com o
 * estado no topo, o problema não chega a existir.
 *
 * Qual aba está aberta sai da **URL**, e não de um `useState`. As duas rotas renderizam
 * este mesmo componente na mesma posição da árvore, então o React não o remonta ao
 * navegar: um estado semeado por prop ficava onde estava, e a tela mostrava uma aba
 * enquanto a barra de endereço dizia a outra. Derivando, o F5, o "voltar" e o link
 * copiado caem todos no mesmo lugar.
 *
 * **A partir da fatia 1 da Fase 14 a tela pertence a uma obra.** A estrutura e a espinha
 * do quantitativo vêm do servidor; o rascunho do navegador guarda só a geometria, que
 * ainda não tem tabela. Sem obra escolhida não há o que montar — e dizer isso é melhor
 * do que abrir uma bancada anônima que não entra em obra nenhuma.
 */
export function TakeoffPage() {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const location = useLocation();
  const navigate = useNavigate();
  const aba = abaDoEndereco(location.pathname);
  // Só as abas marcadas no papel (RN-626). A do endereço que o papel não vê nem chega aqui:
  // a casca leva à primeira que ele vê (RN-629).
  const abasDoPapel = useVisibleTabs('/quantitativo');
  const abas = ABAS.filter((item) => abasDoPapel.some((tab) => tab.id === item.id));
  const [recomecarAberto, setRecomecarAberto] = useState(false);

  const semObra = session === undefined || activeProjectId === undefined;

  const estrutura = useLive(
    () =>
      semObra
        ? Promise.resolve(undefined)
        : container.getProjectStructure.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );

  const quantitativoLido = useLive(
    () =>
      semObra
        ? Promise.resolve(undefined)
        : container.getProjectTakeoff
            .execute(activeProjectId, session)
            // De que obra é, como o desenho abaixo: as tipologias da mesa saem daqui, e o desenho
            // da obra nova não pode ser repartido entre as da anterior (28/10/2026).
            .then((resultado) => ({ de: activeProjectId, resultado })),
    [session?.userId, activeProjectId],
  );
  const quantitativo = quantitativoLido?.resultado;

  /** Quantas vezes o desenho foi relido à força — o "Recarregar o desenho" do conflito. */
  const [leituraDoDesenho, setLeituraDoDesenho] = useState(0);

  /** O desenho: ambiente, parede, vão, pilar e viga (Fase 14, fatia 3 · Fase 15). */
  const desenho = useLive(
    () =>
      semObra
        ? Promise.resolve(undefined)
        : container.getProjectGeometry
            .execute(activeProjectId, session)
            // A leitura diz de que obra é: o `useLive` segura a antiga até a nova chegar, e o
            // desenho de uma obra não pode ser assentado nem gravado como se fosse da outra.
            .then((resultado) => ({ de: activeProjectId, resultado })),
    [session?.userId, activeProjectId, leituraDoDesenho],
  );

  /**
   * O cadastro de serviços da obra — a medida e a espessura de cada um (fatia 4) — e os
   * itens de cada serviço (Fase 24, fatia 3).
   *
   * Numa leitura só, de propósito: os itens são pendurados no catálogo pela chave do
   * serviço, e as duas respostas precisam ser **da mesma obra**. Em duas leituras soltas,
   * trocar de obra deixaria por um instante os serviços de uma com os itens da outra.
   */
  const servicosEItens = useLive(
    () =>
      semObra
        ? Promise.resolve(undefined)
        : Promise.all([
            container.getProjectServices.execute(activeProjectId, session),
            container.getProjectServiceItems.execute(activeProjectId, session),
          ]),
    [session?.userId, activeProjectId],
  );
  const servicos = servicosEItens?.[0];
  const itensDaObra = servicosEItens?.[1];

  const daObra = estrutura?.ok === true ? estrutura.value : undefined;
  const daEspinha = quantitativo?.ok === true ? quantitativo.value : undefined;
  // `null` enquanto não há quantitativo nenhum na mão: nem o da obra anterior.
  const espinhaDe = quantitativoLido?.resultado.ok === true ? String(quantitativoLido.de) : null;
  const daGeometria =
    desenho !== undefined && desenho.de === activeProjectId && desenho.resultado.ok
      ? desenho.resultado.value
      : undefined;
  const doCatalogo = servicos?.ok === true ? servicos.value : undefined;
  const dosItens = itensDaObra?.ok === true ? itensDaObra.value : undefined;

  /**
   * A gravação do desenho — **fora** do `useCommand`, de propósito.
   *
   * `useCommand` avisa o mundo a cada sucesso (`publishChange`), e toda leitura viva
   * rebusca. Numa gravação que acontece sozinha a cada pausa da digitação isso seria um
   * ciclo: grava, rebusca, a bancada se remonta com objetos novos, o efeito de gravar
   * dispara de novo. Aqui não há nada a rebuscar — o cliente acabou de mandar exatamente
   * o que ele já tem na mão.
   *
   * A falha, porém, **não** é silenciosa: montagem que não subiu e não avisou é trabalho
   * perdido com cara de trabalho salvo.
   */
  const gravarDesenho = useCallback(
    async (
      geometria: ProjectGeometry,
      baseRevision?: number,
    ): Promise<ResultadoDaGravacaoDoDesenho | undefined> => {
      if (semObra) return undefined;
      try {
        const resultado = await container.saveProjectGeometry.execute(
          {
            projectId: activeProjectId,
            ...geometria,
            // A revisão sobre a qual a bancada trabalhou: o servidor recusa se ela ficou para
            // trás — outra aba ou outra pessoa gravou no meio (07/10/2026).
            ...(baseRevision === undefined ? {} : { baseRevision }),
          },
          session,
        );
        if (resultado.ok) return { revision: resultado.value.revision };
        // O conflito não é toast que passa: a bancada para de gravar e mostra o aviso fixo.
        if (resultado.error.code === GEOMETRY_CONFLICT) return { conflito: true };
        toast.error(`O desenho não foi salvo: ${resultado.error.message}`);
      } catch {
        toast.error('O desenho não foi salvo — verifique a conexão.');
      }
      return undefined;
    },
    [container, activeProjectId, session, semObra],
  );
  const reler = useCallback(() => {
    setLeituraDoDesenho((n) => n + 1);
  }, []);

  /**
   * A mesma escolha do desenho: fora do `useCommand`, e a falha não é silenciosa. Devolve a
   * gravação para os itens esperarem por ela.
   */
  const gravarCatalogo = useCallback(
    (disciplines: DisciplinasParaGravar) => {
      if (semObra) return;
      return container.applyServiceCatalog
        .execute({ projectId: activeProjectId, disciplines }, session)
        .then((resultado) => {
          if (!resultado.ok) {
            toast.error(`O cadastro de serviços não foi salvo: ${resultado.error.message}`);
            return;
          }
          // O serviço aprovado ficou com a medida de antes (Fase 33, RN-438); o resto gravou.
          const travados = resultado.value.lockedServices;
          if (travados.length > 0) {
            toast.warning(
              `${lockedServicesMessage(travados)} A medida ${travados.length === 1 ? 'dele' : 'deles'} ficou como estava.`,
            );
          }
        })
        .catch(() => {
          toast.error('O cadastro de serviços não foi salvo — verifique a conexão.');
        });
    },
    [container, activeProjectId, session, semObra],
  );

  /**
   * Os itens do serviço (Fase 24, fatia 3), pela mesma porta do cadastro: fora do
   * `useCommand` e com a falha dita.
   *
   * Grava na obra que a bancada diz, e não na aberta: o envelope é de quem o produziu. O
   * serviço que o servidor ainda não conhece não trava o resto, mas conta como não gravado
   * — o envelope sobe de novo na próxima mudança, e aí o serviço já estará lá.
   */
  const gravarItens = useCallback(
    async (projectId: ProjectId, envelope: ItensParaGravar): Promise<boolean> => {
      if (session === undefined) return false;
      try {
        const resultado = await container.saveProjectServiceItems.execute(
          { projectId, ...envelope },
          session,
        );
        if (!resultado.ok) {
          toast.error(`Os itens não foram salvos: ${resultado.error.message}`);
          return false;
        }
        // O serviço aprovado ficou como estava (Fase 33, RN-438), e o resto gravou: o aviso é
        // dito uma vez, e o envelope não volta a subir por causa dele.
        const travados = resultado.value.lockedServices;
        if (travados.length > 0) {
          toast.warning(
            `${lockedServicesMessage(travados)} O que mudou ${travados.length === 1 ? 'nele' : 'neles'} não foi gravado; o resto foi.`,
          );
        }
        const pulados = resultado.value.skippedServices.length;
        if (pulados > 0) {
          toast.warning(
            `${String(pulados)} ${pulados === 1 ? 'serviço ainda não chegou' : 'serviços ainda não chegaram'} à obra; os itens ${pulados === 1 ? 'dele' : 'deles'} vão na próxima gravação.`,
          );
          return false;
        }
        return true;
      } catch {
        toast.error('Os itens não foram salvos — verifique a conexão.');
        return false;
      }
    },
    [container, session],
  );

  // Os serviços aprovados no orçamento (Fase 33): o painel não os põe nem os tira (RN-438), e o
  // desenho que muda a quantidade deles não sobe (RN-439).
  const aprovados = useServicosAprovados();
  const avisarRecusa = useCallback((texto: string) => {
    toast.error(texto);
  }, []);

  const [workspace, dispatch, carregado, conflitoDoDesenho, desenhoTravado, renomearPecaNaMesa] =
    useWorkspaceDraft(daObra, daEspinha, {
      ...(daGeometria === undefined
        ? {}
        : { geometria: daGeometria, geometriaDe: String(activeProjectId) }),
      ...(semObra ? {} : { obra: String(activeProjectId), quantitativoDe: espinhaDe }),
      ...(doCatalogo === undefined ? {} : { servicos: doCatalogo }),
      ...(dosItens === undefined ? {} : { itens: dosItens }),
      salvarGeometria: gravarDesenho,
      reler,
      salvarCatalogo: gravarCatalogo,
      // Sem a leitura dos itens — a API que ainda não tem a `0035`, ou uma recusa —, a
      // bancada volta a guardá-los no navegador em vez de parar de guardá-los em lugar nenhum.
      ...(itensDaObra?.ok === false ? {} : { salvarItens: gravarItens }),
      ...(aprovados === undefined ? {} : { aprovados }),
      avisarRecusa,
    });
  const draft = activeTypology(workspace);

  /**
   * A prancha do desenho em que a tipologia da mesa está (Fase 24). Depois do rascunho, e
   * não antes: é a leitura dele que separa a prancha que o navegador guardava, e que este
   * hook sobe uma vez.
   */
  // As peças da planilha da tipologia na planta (Fase 28, etapa 3): a lista da ferramenta e o
  // ponto de cada uma, gravado na hora.
  const pecasDaPlanilha = usePecasDaPlanilha({
    container,
    session,
    projectId: activeProjectId,
    unitTypeId: draft.unitTypeId,
    // O nome novo da peça (20/10/2026): o servidor grava, e a mesa refaz o mesmo (RN-457).
    aoRenomear: renomearPecaNaMesa,
  });

  const prancha = usePranchaDoPavimento({
    container,
    session,
    projectId: activeProjectId,
    espinha: daEspinha,
    workspace,
    dispatch,
  });

  // As seis escritas da espinha. `useCommand` já revalida a leitura ao terminar, então a
  // tela reflete o servidor sem ninguém sincronizar nada à mão.
  const [ligarPavimento] = useCommand(
    (floorId: string, floorPlanId?: string) =>
      container.linkFloorToFloorPlan.execute(
        {
          projectId: activeProjectId,
          floorId,
          ...(floorPlanId === undefined ? {} : { floorPlanId }),
        },
        session!,
      ),
    { successMessage: 'Pavimento atualizado.', silent: true },
  );
  // Um andar por vez no servidor — o caso de uso é por pavimento —, mas um aviso só na
  // tela: doze "Pavimento atualizado." em fila diriam menos do que "12 pavimentos ligados".
  const [ligarPavimentos] = useCommand(
    async (floorIds: readonly string[], floorPlanId?: string) => {
      for (const floorId of floorIds) {
        const resultado = await container.linkFloorToFloorPlan.execute(
          {
            projectId: activeProjectId,
            floorId,
            ...(floorPlanId === undefined ? {} : { floorPlanId }),
          },
          session!,
        );
        if (!resultado.ok) return resultado;
      }
      return ok(floorIds.length);
    },
    {
      successMessage: (quantos) =>
        `${String(quantos)} ${quantos === 1 ? 'pavimento ligado' : 'pavimentos ligados'}.`,
      silent: true,
    },
  );
  // Fundação e Contenção não têm apto, vaga nem circulação: têm uma área só. Ela nasce junto
  // com o desenho — a mesma tipologia `AREA_COMUM` que o botão "Área comum" cria, com a posição
  // dela no desenho — e a pessoa não precisa saber que existe um passo a mais.
  const [criarDesenho] = useCommand(
    (name: string, kind: FloorPlanKind) =>
      criarDesenhoComAreaUnica(kind, {
        criarDesenho: () =>
          container.createFloorPlan.execute({ projectId: activeProjectId, name, kind }, session!),
        criarAreaUnica: (floorPlanId) =>
          container.createUnitType.execute(
            {
              projectId: activeProjectId,
              name: nomeDaAreaUnica(name, espinhaNomes(workspace)),
              kind: 'AREA_COMUM',
              floorPlanId,
            },
            session!,
          ),
      }),
    { successMessage: 'Pavimento tipo criado.' },
  );
  const [removerDesenho] = useCommand(
    (floorPlanId: string) =>
      container.deleteFloorPlan.execute({ projectId: activeProjectId, floorPlanId }, session!),
    { successMessage: 'Pavimento tipo removido.' },
  );
  const [criarTipologia] = useCommand(
    (input: { name: string; kind: UnitTypeKind; floorPlanId: string; positionCount?: number }) =>
      container.createUnitType.execute({ projectId: activeProjectId, ...input }, session!),
    { successMessage: 'Tipologia criada.' },
  );
  /**
   * "Desenhar este pavimento" (Fase 27, F4): o desenho de Fundação ou Contenção, a área comum
   * dele — onde a estaca mora — e o vínculo dos andares que ainda não apontavam desenho.
   *
   * Reaproveita o desenho que já existe, se houver: só o que falta é criado. Cada passo é o
   * caso de uso que a tela já usa; se um recusa, a mensagem dele é a que a pessoa lê.
   */
  const [desenharPavimento] = useCommand(
    async (kind: FloorPlanKind) => {
      const nome = FLOOR_PLAN_KIND_LABEL[kind];
      let floorPlanId = daEspinha?.floorPlans.find((plano) => plano.kind === kind)?.id as
        string | undefined;
      if (floorPlanId === undefined) {
        const desenho = await container.createFloorPlan.execute(
          { projectId: activeProjectId, name: nome, kind },
          session!,
        );
        if (!desenho.ok) return desenho;
        floorPlanId = desenho.value.id;
      }
      const areaComum = await container.createUnitType.execute(
        { projectId: activeProjectId, name: nome, kind: 'AREA_COMUM', floorPlanId },
        session!,
      );
      if (!areaComum.ok) return areaComum;
      for (const andar of andaresSemDesenho(daObra, kind)) {
        const ligado = await container.linkFloorToFloorPlan.execute(
          { projectId: activeProjectId, floorId: andar.id, floorPlanId },
          session!,
        );
        if (!ligado.ok) return ligado;
      }
      return areaComum;
    },
    { successMessage: 'Pavimento pronto para desenhar.' },
  );
  const [renomearTipologia] = useCommand(
    (unitTypeId: string, name: string) =>
      container.renameUnitType.execute({ projectId: activeProjectId, unitTypeId, name }, session!),
    // Sem `silent`: o nome repetido volta 409, e a mensagem do servidor é a que a pessoa lê.
    { successMessage: 'Tipologia renomeada.' },
  );
  const [removerTipologia] = useCommand(
    (unitTypeId: string) =>
      container.deleteUnitType.execute({ projectId: activeProjectId, unitTypeId }, session!),
    { successMessage: 'Tipologia removida.' },
  );

  const acoes: AcoesDaEspinha = useMemo(
    () => ({
      ligarPavimento: (floorId, floorPlanId) => void ligarPavimento(floorId, floorPlanId),
      ligarPavimentos: (floorIds, floorPlanId) => void ligarPavimentos(floorIds, floorPlanId),
      criarDesenho: (name, kind) => void criarDesenho(name, kind),
      removerDesenho: (floorPlanId) => void removerDesenho(floorPlanId),
      criarTipologia: (input) => void criarTipologia(input),
      renomearTipologia: async (unitTypeId, name) =>
        (await renomearTipologia(unitTypeId, name))?.ok === true,
      removerTipologia: (unitTypeId) => void removerTipologia(unitTypeId),
    }),
    [
      ligarPavimento,
      ligarPavimentos,
      criarDesenho,
      removerDesenho,
      criarTipologia,
      renomearTipologia,
      removerTipologia,
    ],
  );

  /**
   * A conta da tipologia, já contando as divisas com as vizinhas.
   *
   * A divisa entra inteira no desenho — ela cerca o ambiente, dá face e perímetro — mas a
   * **alvenaria** dela se reparte: `ownPositionIds` é o que diz qual metade é desta
   * tipologia, e `neighbourRooms` dá o outro lado à parede sem trazer o piso do vizinho
   * junto (RN-163).
   *
   * Onde há concreto não há bloco: pilar e viga tiram a sombra deles do plano da parede, e o
   * volume sai junto (RN-182, RN-188). A estrutura da divisa entra com a parede dela.
   *
   * A conta mora em `geometriaDaTipologia` desde a Fase 24: a soma da obra precisa do medido
   * de **todas** as tipologias para o índice de consumo, e duas cópias da mesma conta — uma
   * aqui, para a mesa, e outra lá, para a obra — discordariam na primeira mudança.
   */
  const geometry = useMemo(() => geometriaDaTipologia(workspace, draft), [workspace, draft]);
  /**
   * O nome de cada desenho, pelo id — a planilha por peça sugere o destino pelo desenho de
   * mesmo nome que a coluna ANDAR (Fase 28, RN-392).
   */
  const nomesDosDesenhos = useMemo(
    () =>
      new Map<string, string>(
        (daEspinha?.floorPlans ?? []).map((plano) => [String(plano.id), plano.name]),
      ),
    [daEspinha],
  );
  /** Todos os pavimentos, atrelados ou não: o destino da planilha por peça sai do atrelamento. */
  const pavimentosDaObra = useMemo(
    (): readonly PavimentoDaObra[] =>
      (daObra?.tree.towers ?? []).flatMap((torre) =>
        torre.floors.map(({ floor }) => ({
          nome: floor.name,
          torre: torre.tower.name,
          nivel: floor.level,
          ...(floor.floorPlanId === undefined ? {} : { floorPlanId: String(floor.floorPlanId) }),
        })),
      ),
    [daObra],
  );
  const totalTipologia = useMemo(() => summarizeGeometry(geometry), [geometry]);
  const vezesNaObra = floorsOf(workspace, draft);
  const totalPredio = useMemo(
    () => summarizeGeometry(geometry, vezesNaObra),
    [geometry, vezesNaObra],
  );
  const geometriaPorAmbiente = useMemo(
    () => new Map(geometry.rooms.map((room) => [room.roomId, room])),
    [geometry],
  );

  // ── O recorte: o que cada tipologia mostra ─────────────────────────────────
  //
  // O filtro mora no endereço, como a aba: F5, voltar e o link copiado caem no mesmo
  // lugar, e trocar de aba leva o filtro junto.
  const [parametros, setParametros] = useSearchParams();
  const filtro: FiltroDoQuantitativo = useMemo(() => {
    const disciplina = parametros.get('disciplina') ?? undefined;
    const servico = parametros.get('servico') ?? undefined;
    return {
      ...(disciplina === undefined ? {} : { disciplina }),
      ...(disciplina === undefined || servico === undefined ? {} : { servico }),
    };
  }, [parametros]);
  const mudarFiltro = useCallback(
    (proximo: FiltroDoQuantitativo) =>
      setParametros(
        (atual) => {
          const novos = new URLSearchParams(atual);
          novos.delete('disciplina');
          novos.delete('servico');
          if (proximo.disciplina !== undefined) novos.set('disciplina', proximo.disciplina);
          if (proximo.servico !== undefined) novos.set('servico', proximo.servico);
          return novos;
        },
        { replace: true },
      ),
    [setParametros],
  );

  const escopo = useMemo(
    () =>
      daObra === undefined || daEspinha === undefined
        ? undefined
        : montarEscopoDaObra(daObra, daEspinha, doCatalogo ?? []),
    [daObra, daEspinha, doCatalogo],
  );
  /**
   * A unidade de cada serviço é do cadastro (Fase 24, D3): as opções vêm do cadastro de
   * unidades, e o valor de cada linha vem do serviço como a obra o gravou.
   */
  const unidades = useLive(() => container.listUnitsOfMeasure.execute(), []);
  /**
   * O cadastro de serviços da obra, para a planilha plana casar nomes e o modelo percorrer —
   * os serviços que ninguém abriu no quantitativo ainda não estão no catálogo do rascunho.
   */
  const servicosDisponiveis = useMemo(
    () => servicosDaObra(doCatalogo ?? [], daObra?.availableDisciplines ?? [], unidades ?? []),
    [doCatalogo, daObra, unidades],
  );
  /** O que a obra desligou: o catálogo do rascunho ainda guarda, e a planilha não pode usar. */
  const servicosDesativados = useMemo(
    () => servicosDesativadosDaObra(doCatalogo ?? [], daObra?.availableDisciplines ?? []),
    [doCatalogo, daObra],
  );
  const cadastroDoServico = useCallback(
    (serviceKey: string) => escopo?.servicoPorChave.get(serviceKey),
    [escopo],
  );
  const [trocarUnidade] = useCommand(
    (serviceKey: string, unitOfMeasureId: UnitOfMeasureId | undefined) => {
      const gravado = escopo?.servicoPorChave.get(serviceKey);
      if (gravado === undefined) {
        return Promise.resolve(
          err(
            validationError(
              'TAKEOFF.SERVICE_NOT_SAVED',
              'Este serviço ainda está sendo gravado na obra. Aguarde alguns segundos e escolha a unidade de novo.',
            ),
          ),
        );
      }
      return container.setProjectServiceUnit.execute(
        {
          projectId: activeProjectId,
          serviceId: gravado.id,
          ...(unitOfMeasureId === undefined ? {} : { unitOfMeasureId }),
        },
        session!,
      );
    },
    // A recusa é dita por extenso logo abaixo, e não pela mensagem genérica do código: é
    // ela que explica por que a unidade não pode mudar (medição gravada, cadastro global).
    { successMessage: 'Unidade do serviço trocada.', silent: true },
  );
  const aoTrocarUnidade = useCallback(
    (serviceKey: string, unitOfMeasureId: UnitOfMeasureId | undefined) => {
      void trocarUnidade(serviceKey, unitOfMeasureId).then((resultado) => {
        if (resultado !== undefined && !resultado.ok) toast.error(resultado.error.message);
      });
    },
    [trocarUnidade],
  );

  /** As funções que a mão de obra de cada serviço pode apontar (RN-252). */
  const funcoes = useMemo(
    () => funcoesDaObra(dosItens?.jobRoles ?? [], escopo?.servicoPorChave ?? new Map()),
    [dosItens, escopo],
  );
  const catalogoDaObra = workspace.catalog ?? CATALOGO_VAZIO;
  /** Sem o filtro: é dele que saem as opções do próprio filtro e a oferta do painel. */
  const recorteInteiro = useMemo(
    () => (escopo === undefined ? undefined : recortarTipologia(catalogoDaObra, draft, escopo)),
    [catalogoDaObra, draft, escopo],
  );
  const recorte = useMemo(
    () =>
      escopo === undefined ? undefined : recortarTipologia(catalogoDaObra, draft, escopo, filtro),
    [catalogoDaObra, draft, escopo, filtro],
  );
  const recorteDe = useCallback(
    (typology: TypologyDraft): ServiceCatalog =>
      escopo === undefined
        ? catalogoDaObra
        : recortarTipologia(catalogoDaObra, typology, escopo, filtro).catalogo,
    [catalogoDaObra, escopo, filtro],
  );
  /**
   * O orçamento da obra lê o recorte de cada tipologia **sem o filtro do topo**: o filtro
   * escolhe o que as listas mostram, e um custo direto que mudasse com ele seria um total que
   * a obra não tem. E lê as disciplinas na ordem de execução, como as listas ao lado.
   */
  const recorteSemFiltroDe = useCallback(
    (typology: TypologyDraft): ServiceCatalog =>
      escopo === undefined
        ? catalogoDaObra
        : recortarTipologia(catalogoDaObra, typology, escopo).catalogo,
    [catalogoDaObra, escopo],
  );

  // A foto da medida (Fase 40, RN-516): a média do apartamento lê dela. Sobe sozinha depois que
  // a bancada para; a recusa não vira aviso — a foto é derivada, e a próxima pausa tenta de novo.
  const gravarFoto = useCallback(
    async (unitTypes: FotoDaMedida): Promise<boolean> => {
      if (semObra) return false;
      try {
        const resultado = await container.saveTakeoffMeasures.execute(
          { projectId: activeProjectId, unitTypes },
          session,
        );
        return resultado.ok;
      } catch {
        return false;
      }
    },
    [container, activeProjectId, session, semObra],
  );
  // A média do apartamento da obra (Fase 40): lida uma vez aqui, para a tela dos itens e para o
  // fator do tipo nos cômodos.
  const mediaDoApartamento = useMediaDoApartamento();

  /**
   * O cômodo novo, ou que mudou de tipo, ganha o fator da média do tipo dele (Fase 40, RN-528), nos
   * serviços de ambiente que a tipologia escolheu. Só o que mudou desde a última vez: o cômodo que
   * alguém devolveu ao índice não volta sozinho. A troca de tipologia recomeça a comparação.
   */
  const tiposDosComodos = useRef<
    { readonly tipologia: string; readonly tipos: ReadonlyMap<string, string> } | undefined
  >(undefined);
  useEffect(() => {
    const vivos = draft.rooms.filter((room) => room.deletedAt === undefined);
    const tipos = new Map(vivos.map((room) => [String(room.id), room.kind as string]));
    const antes = tiposDosComodos.current;
    tiposDosComodos.current = { tipologia: String(draft.unitTypeId), tipos };
    if (antes === undefined || antes.tipologia !== String(draft.unitTypeId)) return;
    if (mediaDoApartamento === undefined || !carregado) return;
    const mudados = vivos.filter((room) => antes.tipos.get(String(room.id)) !== room.kind);
    if (mudados.length === 0) return;
    const servicos = recorteSemFiltroDe(draft).disciplines.flatMap((d) => d.services);
    fatoresDoTipoNosComodos(mudados, servicos, mediaDoApartamento).forEach((acao) =>
      dispatch(acao),
    );
  }, [draft, mediaDoApartamento, carregado, recorteSemFiltroDe, dispatch]);

  useFotoDaMedida({
    workspace,
    recorteDe: recorteSemFiltroDe,
    projectId: semObra ? undefined : String(activeProjectId),
    ativo: carregado && !conflitoDoDesenho.emConflito && workspace.catalog !== undefined,
    gravar: gravarFoto,
  });
  const catalogoNaOrdemDaObra = useMemo(
    () =>
      escopo === undefined
        ? catalogoDaObra
        : naOrdemDaObra(catalogoDaObra, escopo.ordemDaDisciplina),
    [catalogoDaObra, escopo],
  );
  /**
   * As disciplinas do filtro: todas as dos andares da tipologia, e não só as que já têm
   * serviço escolhido — senão a disciplina que o andar tem, filtrada numa tipologia que
   * ainda não escolheu nada, apareceria como "fora desta tipologia", e não está.
   */
  const disciplinasDoFiltro = useMemo(() => {
    if (escopo === undefined || recorteInteiro === undefined) return [];
    const chaves = new Set([
      ...recorteInteiro.disciplinasDoAndar,
      ...recorteInteiro.catalogo.disciplines.map((disciplina) => disciplina.key),
    ]);
    const nomeNoCatalogo = new Map(
      catalogoDaObra.disciplines.map((disciplina) => [disciplina.key, disciplina.name]),
    );
    return [...chaves]
      .map((key) => ({
        key,
        name: nomeNoCatalogo.get(key) ?? escopo.nomeDaDisciplina.get(key) ?? key,
      }))
      .sort(
        (a, b) =>
          (escopo.ordemDaDisciplina.get(a.key) ?? Number.MAX_SAFE_INTEGER) -
            (escopo.ordemDaDisciplina.get(b.key) ?? Number.MAX_SAFE_INTEGER) ||
          a.name.localeCompare(b.name, 'pt-BR'),
      );
  }, [catalogoDaObra, escopo, recorteInteiro]);

  const oferta = useMemo(
    () =>
      escopo === undefined || recorteInteiro === undefined
        ? []
        : ofertaDoPainel(catalogoDaObra, draft, escopo, recorteInteiro.escolha.chaves),
    [catalogoDaObra, draft, escopo, recorteInteiro],
  );

  /**
   * A média do apartamento nos serviços que a tipologia acabou de escolher (Fase 40, A4, RN-521):
   * os itens da referência que faltam, com o índice médio. É ajuda: sem a média — sem referência,
   * ou a leitura falhou —, a escolha vale do mesmo jeito, e nada é dito.
   */
  const trazerMediaDoApartamento = useCallback(
    async (escolhidos: readonly string[]) => {
      if (semObra || escolhidos.length === 0) return;
      try {
        const media = await container.getApartmentAverage.execute(
          { projectId: activeProjectId },
          session,
        );
        if (!media.ok) return;
        const trazida = trazerAMediaDoApartamento(
          workspace.catalog,
          new Set(escolhidos),
          media.value,
          (serviceKey) => aprovacaoDe(aprovados, serviceKey) !== undefined,
        );
        trazida.acoes.forEach((acao) => dispatch(acao));
        // A média do tipo nos cômodos desta tipologia (etapa 2, RN-528).
        const escolhidosAgora = new Set(escolhidos);
        const servicos = (workspace.catalog?.disciplines ?? [])
          .flatMap((disciplina) => disciplina.services)
          .filter((servico) => escolhidosAgora.has(servico.key));
        fatoresDoTipoNosComodos(draft.rooms, servicos, media.value).forEach((acao) =>
          dispatch(acao),
        );
        if (trazida.acrescentados > 0) {
          toast.success(
            `A média trouxe ${String(trazida.acrescentados)} ${trazida.acrescentados === 1 ? 'item' : 'itens'} da obra de referência, ${String(trazida.comMedia)} com o índice médio.`,
          );
        } else if (trazida.comMedia > 0) {
          toast.success(
            `${String(trazida.comMedia)} ${trazida.comMedia === 1 ? 'item ganhou' : 'itens ganharam'} o índice médio.`,
          );
        }
        // O aço da laje (etapa 3, RN-533) e o da fundação (Fase 41, RN-553): a taxa média nas
        // bitolas que a disciplina não tinha.
        if (trazida.taxas > 0) {
          toast.success(
            `A taxa de aço veio da média em ${String(trazida.taxas)} ${trazida.taxas === 1 ? 'bitola' : 'bitolas'}.`,
          );
        }
      } catch {
        // A média é ajuda: a escolha já foi gravada.
      }
    },
    [
      container,
      activeProjectId,
      session,
      semObra,
      workspace.catalog,
      aprovados,
      dispatch,
      draft.rooms,
    ],
  );

  const [painelAberto, setPainelAberto] = useState(false);
  const [perguntaDaAba, setPerguntaDaAba] = useState<
    { aba: string; servicos: readonly ServicoDaAba[]; base: readonly string[] } | undefined
  >(undefined);

  /**
   * Grava a escolha da tipologia aberta, pelo id de cada serviço.
   *
   * O serviço criado agora no painel pode ainda não ter voltado do servidor com o id; nesse
   * caso a tela pede para esperar em vez de gravar a escolha sem ele.
   */
  const [salvarEscolha, salvarEscolhaState] = useCommand(
    async (chaves: readonly string[], apagar: readonly string[]) => {
      const ids: string[] = [];
      const semId: string[] = [];
      for (const chave of chaves) {
        const gravado = escopo?.servicoPorChave.get(chave);
        if (gravado === undefined) semId.push(chave);
        else ids.push(gravado.id);
      }
      if (semId.length > 0) {
        return err(
          validationError(
            'TAKEOFF.SERVICE_NOT_SAVED',
            'Há serviço novo ainda sendo gravado na obra. Aguarde alguns segundos e salve de novo.',
          ),
        );
      }
      const resultado = await container.setUnitTypeServices.execute(
        { projectId: activeProjectId, unitTypeId: draft.unitTypeId, serviceIds: ids },
        session!,
      );
      if (resultado.ok) {
        for (const serviceKey of apagar) dispatch({ type: 'CLEAR_SERVICE_QUANTITIES', serviceKey });
        // Só o que esta tipologia não tinha: quem tirou um item de propósito não o vê voltar.
        const antes = escopo?.escolhaGravada.get(draft.unitTypeId);
        await trazerMediaDoApartamento(chaves.filter((chave) => antes?.has(chave) !== true));
      }
      return resultado;
    },
    {
      successMessage: 'Serviços da tipologia salvos.',
      silent: true,
      onSuccess: () => {
        setPainelAberto(false);
        setPerguntaDaAba(undefined);
      },
    },
  );

  /** O serviço novo nasce na obra, no servidor, e no catálogo da tela ao mesmo tempo. */
  const [criarServico, criarServicoState] = useCommand(
    (disciplina: { key: string; name: string }, nome: string) => {
      dispatch({ type: 'ADD_DISCIPLINE', name: disciplina.name });
      dispatch({ type: 'ADD_SERVICE', disciplineKey: disciplina.key, name: nome });
      // O mesmo palpite que o `addService` do catálogo faz na tela — medida e peça juntas.
      const measureBy = guessMeasureBy(nome, undefined);
      const appliesTo = guessAppliesTo(measureBy, nome, disciplina.name);
      return container.applyServiceCatalog.execute(
        {
          projectId: activeProjectId,
          disciplines: [
            {
              name: disciplina.name,
              services: [
                { name: nome, measureBy, ...(appliesTo === undefined ? {} : { appliesTo }) },
              ],
            },
          ],
        },
        session!,
      );
    },
    { successMessage: 'Serviço criado na obra.' },
  );

  /**
   * A aba da planilha acabou de ser ligada: pergunta se marca o que ela orça.
   *
   * O recorte aqui ainda é o de **antes** da troca — é ele que diz o que já estava
   * escolhido. Com a escolha nunca gravada, o ponto de partida já passaria a incluir a aba
   * sozinho; perguntar é o que impede isso de valer sem ninguém confirmar.
   */
  const aoLigarAba = useCallback(
    (aba: string) => {
      if (recorteInteiro === undefined) return;
      const jaEscolhidos = recorteInteiro.escolha.gravada
        ? recorteInteiro.escolha.chaves
        : new Set<string>();
      const servicos = servicosDaAba(
        catalogoDaObra,
        aba,
        recorteInteiro.disciplinasDoAndar,
        jaEscolhidos,
      );
      if (servicos.length === 0) return;
      setPerguntaDaAba({ aba, servicos, base: [...recorteInteiro.escolha.chaves] });
    },
    [catalogoDaObra, recorteInteiro],
  );

  // O quantitativo mora numa obra. Sem ela não há estrutura de onde tirar o multiplicador
  // nem onde gravar a tipologia — e uma bancada anônima foi justamente o que esta fatia
  // veio encerrar.
  if (semObra) {
    return (
      <Card>
        <CardContent className="pt-5">
          <EmptyState
            title="Escolha a obra"
            description="O quantitativo pertence a uma obra: a tipologia é dela, e o total é o desenho vezes os pavimentos dela. Escolha a obra no seletor acima para começar."
          />
        </CardContent>
      </Card>
    );
  }

  if (!carregado) {
    return <LoadingState label="Carregando o quantitativo da obra…" />;
  }

  const falhou =
    estrutura?.ok === false
      ? estrutura.error
      : quantitativo?.ok === false
        ? quantitativo.error
        : undefined;
  if (falhou !== undefined) {
    return <ErrorState message={falhou.message} code={falhou.code} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        A faixa é filha direta da página, e não do cabeçalho: `sticky` só acompanha a
        rolagem dentro do próprio pai, e dentro do cabeçalho ela subia junto com ele.
      */}
      <FaixaDoQuantitativo<Aba>
        tipologias={workspace.typologies}
        ativa={draft}
        vezes={vezesNaObra}
        onTipologia={(unitTypeId) => dispatch({ type: 'SELECT_TYPOLOGY', unitTypeId })}
        abas={abas}
        aba={aba}
        onAba={(id) => void navigate({ pathname: CAMINHO_DA_ABA[id], search: location.search })}
        filtro={filtro}
        disciplinas={disciplinasDoFiltro}
        servicos={(
          recorteInteiro?.catalogo.disciplines.find(
            (disciplina) => disciplina.key === filtro.disciplina,
          )?.services ?? []
        ).map((servico) => ({ key: servico.key, name: servico.name }))}
        onFiltro={mudarFiltro}
        acoes={
          // Zerar a bancada inteira num clique não: é a estrutura, as tipologias, o
          // cadastro e o que foi lançado, tudo de uma vez e sem desfazer.
          <Button variant="ghost" onClick={() => setRecomecarAberto(true)}>
            <RotateCcw className="size-4" /> Recomeçar
          </Button>
        }
      />

      <header className="flex flex-col gap-2">
        <p className="text-sm text-(--color-content-muted)">
          {aba === 'MONTAGEM'
            ? 'Digite largura e comprimento de cada ambiente; o sistema cria as quatro paredes e você aponta quais são a mesma.'
            : aba === 'ITENS'
              ? 'A quantidade de cada material dentro do serviço, e o quantitativo somado item a item.'
              : 'O custo direto da obra inteira — material e mão de obra, sem BDI —, disciplina por disciplina e serviço por serviço.'}
        </p>

        {/*
          O aviso mudou de assunto porque a tela mudou de natureza. Antes tudo era
          rascunho; agora a espinha — pavimento tipo, tipologia e o vínculo com o andar —
          é da obra, e o que segue no navegador é a geometria, que ainda não tem tabela.
        */}
        <p className="flex items-start gap-2 text-xs text-(--color-content-muted)">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>
            <strong className="font-medium text-(--color-content)">
              Pavimentos, tipologias, desenho, serviços e itens
            </strong>{' '}
            ficam gravados na obra.{' '}
            <strong className="font-medium text-(--color-content)">
              A prancha carregada e a divergência de cada apto
            </strong>{' '}
            ainda são rascunho deste navegador.
          </span>
        </p>

        {/* Sem a leitura dos itens, a bancada volta a guardá-los aqui — e diz isso, em vez
            de deixar parecer que foram gravados na obra. */}
        {itensDaObra?.ok === false && (
          <p
            role="alert"
            className="rounded-md border border-(--color-state-warn) px-3 py-2 text-sm"
          >
            Os itens dos serviços não puderam ser lidos da obra ({itensDaObra.error.message}).
            Enquanto isso, o que for lançado fica guardado só neste navegador.
          </p>
        )}

        {/* O desenho mudou em outra aba ou por outra pessoa: a gravação automática parou, e a
            única saída é reler — o que descarta a mesa, e por isso o aviso diz antes. */}
        {conflitoDoDesenho.emConflito && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-(--color-state-danger) px-3 py-2 text-sm"
          >
            <p className="min-w-0 flex-1">
              O desenho desta obra mudou em outra aba ou por outra pessoa desde que você abriu. Sua
              última alteração não foi gravada e o desenho parou de ser gravado nesta aba.
              Recarregar traz a versão atual e descarta o que está na mesa — a última alteração se
              perde.
            </p>
            <Button variant="secondary" onClick={conflitoDoDesenho.recarregar}>
              Recarregar o desenho
            </Button>
          </div>
        )}

        {/* O desenho mexeu na quantidade de um serviço aprovado (Fase 33, RN-439): a gravação
            parou, e o aviso fica até o desenho voltar a dar a quantidade de antes. */}
        {desenhoTravado.mudancas.length > 0 && (
          <AvisoDoDesenhoTravado
            mudancas={desenhoTravado.mudancas}
            desfazer={desenhoTravado.desfazer}
          />
        )}

        {/* Os dois canais na mesma faixa: a recusa da tipologia e a da bancada. Quem lê
            não precisa saber de qual redutor o "não" veio. */}
        {(workspace.lastError ?? draft.lastError) !== undefined && (
          <p
            role="alert"
            className="rounded-md border border-(--color-state-danger) px-3 py-2 text-sm"
          >
            {workspace.lastError ?? draft.lastError}
          </p>
        )}

        {aba === 'MONTAGEM' && (
          <EtapasDaMontagem
            estrutura={daObra!}
            espinha={daEspinha!}
            workspace={workspace}
            draft={draft}
            escolhidos={recorteInteiro?.escolha.chaves.size ?? 0}
          />
        )}
      </header>

      {aba === 'ITENS' ? (
        <TabPanel base="quantitativo" id="ITENS">
          {recorte !== undefined && (
            <ItensEConsolidacao
              {...(mediaDoApartamento === undefined ? {} : { mediaDoApartamento })}
              workspace={workspace}
              draft={draft}
              tipologia={totalTipologia}
              dispatch={dispatch}
              recorte={recorte}
              recorteDe={recorteDe}
              filtrado={filtro.disciplina !== undefined}
              onLimparFiltro={() => mudarFiltro({})}
              onEscolherServicos={() => setPainelAberto(true)}
              onAbrirEstrutura={() => void navigate('/estrutura')}
              funcoes={funcoes}
            />
          )}
        </TabPanel>
      ) : aba === 'ORCAMENTO' ? (
        <TabPanel base="quantitativo" id="ORCAMENTO">
          <OrcamentoDaObra
            workspace={workspace}
            catalogo={catalogoNaOrdemDaObra}
            recorteDe={recorteSemFiltroDe}
            onAbrirItens={() =>
              void navigate({ pathname: CAMINHO_DA_ABA.ITENS, search: location.search })
            }
          />
        </TabPanel>
      ) : (
        <TabPanel
          base="quantitativo"
          id="MONTAGEM"
          className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]"
        >
          {/*
          `min-w-0` não é enfeite: item de grid nasce com `min-width: auto` e se recusa a
          encolher abaixo do conteúdo. A tabela do orçamento tem oito colunas, e sem isto
          é a **página** que passa a rolar na horizontal em vez da tabela — que é
          justamente o que o `TableWrapper` existe para impedir (PLANO_GERAL §4.4).
        */}
          <div className="flex min-w-0 flex-col gap-4">
            <div id="etapa-pavimentos" className="scroll-mt-4">
              <EstruturaCard estrutura={daObra!} espinha={daEspinha!} acoes={acoes} />
            </div>
            <div id="etapa-tipologias" className="scroll-mt-4">
              <TipologiasCard
                workspace={workspace}
                espinha={daEspinha!}
                dispatch={dispatch}
                acoes={acoes}
              />
            </div>
            {hasStructure(workspace) && (
              <>
                <div id="etapa-planta" className="scroll-mt-4">
                  <PlanTracer
                    workspace={workspace}
                    draft={draft}
                    dispatch={dispatch}
                    prancha={prancha}
                    {...(activeProjectId === undefined ? {} : { projectId: activeProjectId })}
                    pavimentosSemDesenho={pavimentosSemDesenhoDaObra({
                      estrutura: daObra,
                      desenhos: daEspinha?.floorPlans ?? [],
                      tipologias: workspace.typologies,
                    })}
                    aoDesenharPavimento={(kind) => void desenharPavimento(kind)}
                    pecasDaPlanilha={pecasDaPlanilha}
                    mediaDoApartamento={mediaDoApartamento}
                    aprovados={aprovados}
                  />
                </div>
                <div id="etapa-ambientes" className="scroll-mt-4">
                  <AmbientesCard
                    draft={draft}
                    dispatch={dispatch}
                    porAmbiente={geometriaPorAmbiente}
                    catalogo={workspace.catalog}
                  />
                </div>
                <div id="etapa-paredes" className="scroll-mt-4">
                  <ParedesCard workspace={workspace} draft={draft} dispatch={dispatch} />
                </div>
                <div id="etapa-servicos" className="flex scroll-mt-4 flex-col gap-4">
                  <ServiceSheetImport
                    catalog={workspace.catalog}
                    dispatch={dispatch}
                    disponiveis={servicosDisponiveis}
                    desativados={servicosDesativados}
                    workspace={workspace}
                    projectId={activeProjectId}
                    nomesDosDesenhos={nomesDosDesenhos}
                    pavimentosDaObra={pavimentosDaObra}
                    funcoesDoServico={funcoes.doServico}
                  />
                  {/*
                    O cadastro de disciplina e serviço saiu daqui: o catálogo da obra é
                    biblioteca, e a tipologia escolhe nele o que usa — no painel, que também
                    cria o serviço que faltar.
                  */}
                  <Card>
                    <CardContent className="flex flex-wrap items-center gap-3 pt-5">
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="text-sm font-medium">
                          {(recorteInteiro?.escolha.chaves.size ?? 0) === 0
                            ? `Nenhum serviço escolhido em ${draft.name}`
                            : `${String(recorteInteiro?.escolha.chaves.size ?? 0)} serviços escolhidos em ${draft.name}`}
                        </span>
                        <span className="text-xs text-(--color-content-muted)">
                          Só o escolhido aparece no orçamento abaixo e na aba de itens.
                        </span>
                      </div>
                      <Button variant="secondary" onClick={() => setPainelAberto(true)}>
                        Escolher serviços
                      </Button>
                    </CardContent>
                  </Card>
                </div>
                <div id="etapa-orcamento" className="scroll-mt-4">
                  <OrcamentoCard
                    catalog={workspace.catalog === undefined ? undefined : recorte?.catalogo}
                    onAbaLigada={aoLigarAba}
                    draft={draft}
                    tipologia={totalTipologia}
                    predio={totalPredio}
                    floors={vezesNaObra}
                    dispatch={dispatch}
                    unidades={unidades ?? []}
                    cadastroDoServico={cadastroDoServico}
                    onTrocarUnidade={aoTrocarUnidade}
                  />
                </div>
              </>
            )}
          </div>

          <ResultadoCard
            workspace={workspace}
            draft={draft}
            tipologia={totalTipologia}
            predio={totalPredio}
          />
        </TabPanel>
      )}

      <EscolherServicos
        open={painelAberto}
        onOpenChange={setPainelAberto}
        tipologia={draft.name}
        oferta={oferta}
        escolhidos={recorteInteiro?.escolha.chaves ?? new Set<string>()}
        gravada={recorteInteiro?.escolha.gravada ?? false}
        salvando={salvarEscolhaState.isPending}
        erro={salvarEscolhaState.error?.message}
        onSalvar={(chaves, apagar) => void salvarEscolha(chaves, apagar)}
        onCriar={(disciplina, nome) => void criarServico(disciplina, nome)}
        criando={criarServicoState.isPending}
        aprovados={aprovados}
      />

      {perguntaDaAba !== undefined && (
        <MarcarDaPlanilha
          open
          onOpenChange={(aberto) => {
            if (!aberto) setPerguntaDaAba(undefined);
          }}
          aba={perguntaDaAba.aba}
          tipologia={draft.name}
          servicos={perguntaDaAba.servicos}
          salvando={salvarEscolhaState.isPending}
          onConfirmar={(chaves) => void salvarEscolha([...perguntaDaAba.base, ...chaves], [])}
        />
      )}

      <ConfirmDialog
        open={recomecarAberto}
        onOpenChange={setRecomecarAberto}
        title="Recomeçar do zero?"
        description="Sai tudo: torres e pavimentos, as tipologias com os ambientes e as paredes, o cadastro de serviços e o que foi lançado nos itens. A mudança é gravada na obra, e não há como desfazer."
        confirmLabel="Recomeçar"
        destructive
        onConfirm={() => {
          dispatch({ type: 'RESET' });
          setRecomecarAberto(false);
        }}
      />
    </div>
  );
}

type Dispatch = React.Dispatch<WorkspaceAction>;

// ── Estrutura e espinha, agora da obra ──────────────────────────────────────────

/**
 * A estrutura da obra, e qual desenho cada pavimento usa.
 *
 * **Deixou de cadastrar torre e pavimento.** Eles são da Fase 2 e já existiam; ter um
 * segundo cadastro aqui criava dois prédios que podiam divergir — e o quantitativo sairia
 * do errado. O que esta tela faz é a única coisa que só ela sabe: dizer qual **desenho**
 * cada andar usa. É desse apontamento que o multiplicador sai (RN-167).
 */
/**
 * O aviso fixo do desenho travado (Fase 33, RN-439), como o do conflito de revisão: diz o
 * serviço e a diferença, e oferece desfazer. Some sozinho quando o desenho volta a dar a
 * quantidade de antes.
 */
function AvisoDoDesenhoTravado({
  mudancas,
  desfazer,
}: {
  mudancas: readonly MudancaNoAprovado[];
  desfazer: () => void;
}) {
  const { textos, mais } = primeirasMudancas(mudancas);
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-(--color-state-danger) px-3 py-2 text-sm"
    >
      <div className="min-w-0 flex-1">
        <p>
          A última mudança no desenho muda a quantidade de serviço aprovado no orçamento, que não
          muda mais. Ela não foi gravada, e o desenho parou de ser gravado até a quantidade voltar a
          ser a de antes:
        </p>
        <ul className="mt-1 list-disc pl-5">
          {textos.map((texto) => (
            <li key={texto}>{texto}</li>
          ))}
          {mais > 0 && <li>e mais {mais}</li>}
        </ul>
      </div>
      <Button variant="secondary" onClick={desfazer}>
        Desfazer a mudança
      </Button>
    </div>
  );
}

function EstruturaCard({
  estrutura,
  espinha,
  acoes,
}: {
  estrutura: ProjectStructureView;
  espinha: ProjectTakeoff;
  acoes: AcoesDaEspinha;
}) {
  const ligados = estrutura.tree.towers
    .flatMap((torre) => torre.floors)
    .filter((node) => node.floor.floorPlanId !== undefined).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pavimentos e desenhos</CardTitle>
        <CardDescription>
          Cada andar aponta o desenho que usa.
          {ligados > 0 &&
            ` Hoje são ${String(ligados)} ${ligados === 1 ? 'pavimento ligado' : 'pavimentos ligados'}.`}
        </CardDescription>
        <ComoFunciona>
          Torres e pavimentos vêm da <strong>Estrutura</strong> da obra; aqui você só diz qual
          desenho cada andar usa. Doze andares apontando o mesmo desenho valem doze no quantitativo
          — é assim que o multiplicador sai da estrutura em vez de ser digitado.
        </ComoFunciona>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estrutura.tree.towers.length === 0 ? (
          <EmptyState
            title="A obra ainda não tem torre"
            description="Cadastre torre e pavimentos na tela de Estrutura. O quantitativo lê de lá — aqui você só diz qual desenho cada andar usa."
          />
        ) : (
          <>
            {/* Os desenhos vêm antes das torres: é neles que os andares se penduram. */}
            <NovoDesenho espinha={espinha} acoes={acoes} />
            {estrutura.tree.towers.map((torre) => (
              <TorreBloco key={torre.tower.id} torre={torre} espinha={espinha} acoes={acoes} />
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function TorreBloco({
  torre,
  espinha,
  acoes,
}: {
  torre: ProjectStructureView['tree']['towers'][number];
  espinha: ProjectTakeoff;
  acoes: AcoesDaEspinha;
}) {
  // Do térreo para cima, como a estrutura ordena — e a lista mostra de cima para baixo,
  // como o corte do prédio.
  const pavimentos = [...torre.floors].sort((a, b) => a.floor.level - b.floor.level);
  const ligados = pavimentos.filter((node) => node.floor.floorPlanId !== undefined).length;

  return (
    <section className="flex flex-col gap-3 rounded-md border border-(--color-border-subtle) p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">{torre.tower.name}</h3>
        <span className="tabular text-xs text-(--color-content-muted)">
          {pavimentos.length} {pavimentos.length === 1 ? 'pavimento' : 'pavimentos'} · {ligados} com
          desenho
        </span>
      </div>

      {pavimentos.length === 0 ? (
        <p className="rounded-md border border-dashed border-(--color-border-subtle) px-3 py-2 text-xs text-(--color-content-muted)">
          Nenhum pavimento nesta torre. Cadastre-os na tela de Estrutura.
        </p>
      ) : (
        <>
          {espinha.floorPlans.length > 0 && pavimentos.length > 1 && (
            <LigarEmLote pavimentos={pavimentos} espinha={espinha} acoes={acoes} />
          )}

          <ul className="divide-y divide-(--color-border-subtle)">
            {[...pavimentos].reverse().map((node) => (
              <li
                key={node.floor.id}
                className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-sm"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="flex h-5 min-w-6 shrink-0 items-center justify-center rounded border border-(--color-border-subtle) px-1 font-mono text-[11px] font-semibold leading-none tabular text-(--color-content-muted)"
                  >
                    {node.floor.level}
                  </span>
                  <span className="font-medium">{node.floor.name}</span>
                  {node.floor.floorPlanId === undefined && (
                    <Badge tone="neutral">fora do quantitativo</Badge>
                  )}
                </span>

                <select
                  aria-label={`Desenho de ${node.floor.name}`}
                  className={SELECT_CLASS}
                  value={node.floor.floorPlanId ?? ''}
                  onChange={(event) =>
                    acoes.ligarPavimento(
                      node.floor.id,
                      event.target.value === '' ? undefined : event.target.value,
                    )
                  }
                >
                  <option value="">— sem desenho —</option>
                  {espinha.floorPlans.map((plano) => (
                    <option key={plano.id} value={plano.id}>
                      {plano.name} · {FLOOR_PLAN_KIND_LABEL[plano.kind]}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/**
 * Ligar um desenho a uma faixa de andares de uma vez.
 *
 * Doze andares tipo apontando o mesmo desenho eram doze cliques na mesma lista. Aqui é a
 * faixa — do 1º ao 12º — e o desenho; o botão diz quantos vai ligar antes de ligar. A
 * faixa é por nível, e invertê-la não é erro: de cima para baixo dá no mesmo.
 */
function LigarEmLote({
  pavimentos,
  espinha,
  acoes,
}: {
  pavimentos: readonly ProjectStructureView['tree']['towers'][number]['floors'][number][];
  espinha: ProjectTakeoff;
  acoes: AcoesDaEspinha;
}) {
  const id = useId();
  const [de, setDe] = useState<string>(pavimentos[0]!.floor.id);
  const [ate, setAte] = useState<string>(pavimentos[pavimentos.length - 1]!.floor.id);
  const [desenho, setDesenho] = useState<string>(espinha.floorPlans[0]!.id);

  // Andar ou desenho que sumiu no meio do caminho cai no primeiro da lista, em vez de
  // deixar o formulário apontando para o nada.
  const inicio = Math.max(
    0,
    pavimentos.findIndex((node) => node.floor.id === de),
  );
  const fim = Math.max(
    0,
    pavimentos.findIndex((node) => node.floor.id === ate),
  );
  const faixa = pavimentos.slice(Math.min(inicio, fim), Math.max(inicio, fim) + 1);
  const plano = espinha.floorPlans.find((item) => item.id === desenho) ?? espinha.floorPlans[0]!;

  return (
    <form
      className="flex flex-wrap items-end gap-2 rounded-md bg-(--color-surface-muted) px-3 py-2"
      onSubmit={(event) => {
        event.preventDefault();
        acoes.ligarPavimentos(
          faixa.map((node) => node.floor.id),
          plano.id,
        );
      }}
    >
      <span className="w-full text-xs font-medium text-(--color-content-muted)">
        Ligar vários andares ao mesmo desenho
      </span>
      <Field label="De" htmlFor={`${id}-de`}>
        <select
          id={`${id}-de`}
          className={SELECT_CLASS}
          value={pavimentos[inicio]!.floor.id}
          onChange={(event) => setDe(event.target.value)}
        >
          {pavimentos.map((node) => (
            <option key={node.floor.id} value={node.floor.id}>
              {node.floor.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Até" htmlFor={`${id}-ate`}>
        <select
          id={`${id}-ate`}
          className={SELECT_CLASS}
          value={pavimentos[fim]!.floor.id}
          onChange={(event) => setAte(event.target.value)}
        >
          {pavimentos.map((node) => (
            <option key={node.floor.id} value={node.floor.id}>
              {node.floor.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Desenho" htmlFor={`${id}-desenho`}>
        <select
          id={`${id}-desenho`}
          className={SELECT_CLASS}
          value={plano.id}
          onChange={(event) => setDesenho(event.target.value)}
        >
          {espinha.floorPlans.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name} · {FLOOR_PLAN_KIND_LABEL[item.kind]}
            </option>
          ))}
        </select>
      </Field>
      <Button type="submit" variant="secondary" disabled={faixa.length === 0}>
        <Link2 className="size-4" /> Ligar {faixa.length}{' '}
        {faixa.length === 1 ? 'pavimento' : 'pavimentos'}
      </Button>
    </form>
  );
}

/** Cadastrar o desenho — o que não existe em lugar nenhum senão aqui. */
export function NovoDesenho({
  espinha,
  acoes,
}: {
  espinha: ProjectTakeoff;
  acoes: AcoesDaEspinha;
}) {
  const [nome, setNome] = useState('');
  const [kind, setKind] = useState<FloorPlanKind>('TIPO');
  const [removendo, setRemovendo] = useState<string | undefined>(undefined);

  /** As tipologias que moram no desenho — as que saem junto com ele. */
  const doDesenho = (floorPlanId: string): string[] => {
    const ids = new Set(
      espinha.positions
        .filter((posicao) => posicao.floorPlanId === floorPlanId)
        .map((posicao) => posicao.unitTypeId),
    );
    return espinha.unitTypes
      .filter((tipologia) => ids.has(tipologia.id))
      .map((tipologia) => tipologia.name);
  };
  const aRemover = espinha.floorPlans.find((plano) => plano.id === removendo);
  const saem = aRemover === undefined ? [] : doDesenho(aRemover.id);

  return (
    <div className="flex flex-col gap-2 border-b border-(--color-border-subtle) pb-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)">
        Desenhos de pavimento
      </p>
      {espinha.floorPlans.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {espinha.floorPlans.map((plano) => (
            <li
              key={plano.id}
              className="flex items-center gap-2 rounded-md border border-(--color-border-subtle) px-2 py-1 text-xs"
            >
              <span className="font-medium">{plano.name}</span>
              <span className="text-(--color-content-muted)">
                {FLOOR_PLAN_KIND_LABEL[plano.kind]}
              </span>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remover ${plano.name}`}
                onClick={() => setRemovendo(plano.id)}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {/*
        Remover o desenho leva as tipologias dele — e o traçado que cada uma tem. Antes elas
        ficavam vivas e sem lugar, e apareciam em outro desenho, inflando o multiplicador.
      */}
      <ConfirmDialog
        open={aRemover !== undefined}
        onOpenChange={(aberto) => {
          if (!aberto) setRemovendo(undefined);
        }}
        title={`Remover ${aRemover?.name ?? 'o desenho'}?`}
        description={
          saem.length === 0
            ? 'O desenho não tem tipologia. Não há como desfazer.'
            : `Saem junto as tipologias dele (${saem.join(', ')}) e o traçado delas: ambientes, paredes e o que foi lançado. Não há como desfazer.`
        }
        confirmLabel="Remover"
        destructive
        onConfirm={() => {
          if (aRemover !== undefined) acoes.removerDesenho(aRemover.id);
          setRemovendo(undefined);
        }}
      />

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (nome.trim() === '') return;
          acoes.criarDesenho(nome, kind);
          setNome('');
        }}
      >
        <Field label="Novo desenho" htmlFor="desenho-nome">
          <Input
            id="desenho-nome"
            value={nome}
            placeholder="Pavimento tipo"
            className="w-56"
            onChange={(event) => setNome(event.target.value)}
          />
        </Field>
        <Field label="Tipo" htmlFor="desenho-tipo">
          <select
            id="desenho-tipo"
            className={SELECT_CLASS}
            value={kind}
            onChange={(event) => setKind(event.target.value as FloorPlanKind)}
          >
            {FLOOR_PLAN_KIND_ORDER.map((item) => (
              <option key={item} value={item}>
                {FLOOR_PLAN_KIND_LABEL[item]}
              </option>
            ))}
          </select>
        </Field>
        <Button type="submit" variant="secondary" disabled={nome.trim() === ''}>
          <Plus className="size-4" /> Desenho
        </Button>
      </form>
    </div>
  );
}

// ── Tipologias ──────────────────────────────────────────────────────────────────

function TipologiasCard({
  workspace,
  espinha,
  dispatch,
  acoes,
}: {
  workspace: WorkspaceDraft;
  espinha: ProjectTakeoff;
  dispatch: Dispatch;
  acoes: AcoesDaEspinha;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Tipologias, por pavimento</CardTitle>
        <CardDescription>
          Monte uma tipologia de cada vez: o quantitativo mora nela, e o apto de cada andar herda.
        </CardDescription>
        <ComoFunciona>
          Cada desenho tem as tipologias dele. O tipo se repete andar após andar; o garden, a
          cobertura, o térreo e a garagem acontecem o número de vezes que a estrutura disser. Cada
          pavimento abre o que cabe nele: a garagem não tem apto e tem vaga, o tipo tem apto e não
          tem vaga, e área comum todo andar tem.
        </ComoFunciona>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {espinha.floorPlans.length === 0 ? (
          <EmptyState
            title="Cadastre um desenho primeiro"
            description="A tipologia mora num desenho de pavimento: é ele que diz quantas vezes ela acontece. Cadastre um acima."
          />
        ) : (
          espinha.floorPlans.map((plano) => (
            <PavimentoBloco
              key={plano.id}
              plano={plano}
              workspace={workspace}
              dispatch={dispatch}
              acoes={acoes}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

// ── Ambientes ───────────────────────────────────────────────────────────────────

function AmbientesCard({
  draft,
  dispatch,
  porAmbiente,
  catalogo,
}: {
  draft: TypologyDraft;
  dispatch: Dispatch;
  porAmbiente: ReadonlyMap<RoomId, RoomGeometry>;
  /** O cadastro da obra, para os fatores do cômodo (Fase 39, RN-512). Ausente, não aparecem. */
  catalogo: ServiceCatalog | undefined;
}) {
  const [nome, setNome] = useState('');
  const [kind, setKind] = useState<RoomKind>('QUARTO');
  const [largura, setLargura] = useState('3');
  const [comprimento, setComprimento] = useState('4');
  const [altura, setAltura] = useState(String(DEFAULT_WALL_HEIGHT));
  const [peDireito, setPeDireito] = useState(String(DEFAULT_CEILING_HEIGHT));

  function adicionar() {
    dispatch({
      type: 'ADD_ROOM',
      name: nome.trim() === '' ? ROOM_KIND_LABEL[kind] : nome,
      kind,
      width: Number(largura.replace(',', '.')),
      length: Number(comprimento.replace(',', '.')),
      wallHeight: Number(altura.replace(',', '.')),
      ceilingHeight: Number(peDireito.replace(',', '.')),
    });
    setNome('');
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ambientes de {draft.name}</CardTitle>
        <CardDescription>
          Largura × comprimento dá piso e teto; a altura mede a alvenaria e o pé-direito, o
          revestimento.
        </CardDescription>
        <ComoFunciona>
          Cada ambiente nasce com quatro paredes externas. <strong>Altura</strong> é o quanto a
          parede sobe até a laje, e é ela que mede a alvenaria: comprimento × altura.{' '}
          <strong>Pé-direito</strong> é o que se enxerga de dentro, até o forro, e mede o
          revestimento. Os dois são medidas diferentes da mesma parede — a alvenaria segue além do
          forro, e é por isso que a altura é maior.
        </ComoFunciona>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Nome" htmlFor="ambiente-nome">
            <Input
              id="ambiente-nome"
              value={nome}
              placeholder={ROOM_KIND_LABEL[kind]}
              onChange={(event) => setNome(event.target.value)}
              className="w-40"
            />
          </Field>
          <Field label="Tipo" htmlFor="ambiente-tipo">
            <select
              id="ambiente-tipo"
              value={kind}
              onChange={(event) => setKind(event.target.value as RoomKind)}
              className={SELECT_CLASS}
            >
              {ROOM_KINDS.map((item) => (
                <option key={item} value={item}>
                  {ROOM_KIND_LABEL[item]}
                </option>
              ))}
            </select>
          </Field>
          <Medida label="Largura" id="ambiente-largura" value={largura} onChange={setLargura} />
          <Medida
            label="Comprimento"
            id="ambiente-comprimento"
            value={comprimento}
            onChange={setComprimento}
          />
          {/* A altura vem antes do pé-direito porque é a maior das duas, e a ordem ajuda a
              ler que uma passa da outra. */}
          <Medida label="Altura" id="ambiente-altura" value={altura} onChange={setAltura} />
          <Medida label="Pé-direito" id="ambiente-pe" value={peDireito} onChange={setPeDireito} />
          <Button onClick={adicionar}>
            <Plus className="size-4" /> Adicionar
          </Button>
        </div>

        {draft.rooms.length === 0 ? (
          <EmptyState
            title="Nenhum ambiente ainda"
            description="Comece pela sala ou pelo quarto — a ordem não importa."
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {draft.rooms.map((room) => {
              const medida = porAmbiente.get(room.id);
              return (
                <li
                  key={room.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-(--color-border-subtle) px-3 py-2"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{room.name}</span>
                    <span className="text-xs text-(--color-content-muted)">
                      {ROOM_KIND_LABEL[room.kind]} · {metros(room.width)} × {metros(room.length)} m
                      · pé-direito {metros(room.ceilingHeight)} m
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <GeometriaDoAmbiente room={room} medida={medida} dispatch={dispatch} />
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remover ${room.name}`}
                      onClick={() => dispatch({ type: 'REMOVE_ROOM', roomId: room.id })}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                  {catalogo !== undefined && (
                    <div className="w-full">
                      <FatoresProprios
                        idBase={`ambiente-${room.id}-fator`}
                        titulo={`Fatores de ${room.name}`}
                        servicos={fatoresDoComodo(catalogo, room)}
                        rotuloDaVolta="Voltar ao índice"
                        vazio="Nenhum serviço da obra desce a cômodo com item a converter."
                        onMudar={(serviceKey, itemKey, factor) =>
                          dispatch({
                            type: 'SET_ROOM_FACTOR',
                            roomId: room.id,
                            serviceKey,
                            itemKey,
                            ...(factor === undefined ? {} : { factor }),
                          })
                        }
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Medida({
  label,
  id,
  value,
  onChange,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label} htmlFor={id}>
      <Input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-24"
      />
    </Field>
  );
}

/** Área e perímetro do ambiente, corrigíveis — a sala em L não é retângulo (RN-157). */
function GeometriaDoAmbiente({
  room,
  medida,
  dispatch,
}: {
  room: Room;
  medida: RoomGeometry | undefined;
  dispatch: Dispatch;
}) {
  const corrigida = room.areaOverride !== undefined || room.perimeterOverride !== undefined;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-(--color-content-muted)">
      <label className="flex items-center gap-1">
        piso
        <NumeroInput
          aria-label={`Área de piso de ${room.name}`}
          value={medida?.floorArea ?? 0}
          onChange={(areaOverride) =>
            dispatch({ type: 'UPDATE_ROOM', roomId: room.id, patch: { areaOverride } })
          }
          className="w-20"
        />
        m²
      </label>
      <label className="flex items-center gap-1">
        perímetro
        <NumeroInput
          aria-label={`Perímetro de ${room.name}`}
          value={medida?.perimeter ?? 0}
          onChange={(perimeterOverride) =>
            dispatch({ type: 'UPDATE_ROOM', roomId: room.id, patch: { perimeterOverride } })
          }
          className="w-20"
        />
        m
      </label>
      <span>
        face <strong className="text-(--color-content)">{metros(medida?.faceArea ?? 0)} m²</strong>
      </span>
      <span>{ORIGEM_DA_AREA[medida?.areaSource ?? 'MEDIDAS']}</span>
      {corrigida && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => dispatch({ type: 'RESET_ROOM_GEOMETRY', roomId: room.id })}
        >
          <RotateCcw className="size-4" /> Voltar ao calculado
        </Button>
      )}
    </div>
  );
}

// ── Paredes ─────────────────────────────────────────────────────────────────────

/** O nome do ambiente de um lado da parede, para a escolha do avanço dizer para onde. */
function nomeDoLado(draft: TypologyDraft, roomId: RoomId): string {
  return draft.rooms.find((room) => room.id === roomId)?.name ?? 'o outro lado';
}

/**
 * O pé-direito de cada lado da parede — a altura que a **face** tem (RN-162).
 *
 * A divisa encara ambientes de tipologias diferentes, então a busca é na bancada inteira e
 * não na tipologia da mesa. Do lado da rua não há ambiente: ali a face é a fachada, e ela
 * vai até a laje.
 */
function peDireitoDosLados(
  workspace: WorkspaceDraft,
  wall: Wall,
): { readonly A: number; readonly B: number } {
  const ambientes = workspace.typologies.flatMap((typology) => typology.rooms);
  const doLado = (roomId: RoomId | undefined): number =>
    (roomId === undefined
      ? undefined
      : ambientes.find((room) => room.id === roomId)?.ceilingHeight) ?? wall.height;

  return { A: doLado(wall.sideA.roomId), B: doLado(wall.sideB?.roomId) };
}

function ParedesCard({
  workspace,
  draft,
  dispatch,
}: {
  workspace: WorkspaceDraft;
  draft: TypologyDraft;
  dispatch: Dispatch;
}) {
  const [comCasadas, setComCasadas] = useState<ReadonlySet<string>>(new Set());
  const abertas = draft.walls.filter((wall) => wall.sideB === undefined).length;

  const divisas = partyWallsTouching(workspace, draft);
  const grupos = groupWallsByRoom(draft, divisas);
  // O pilar e a viga com contorno vivem sem parede: nenhuma linha de parede os mostra.
  const comTrecho = new Set(draft.elementSegments.map((segment) => segment.elementId));
  const soltos = draft.elements.filter((element) => !comTrecho.has(element.id));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Paredes</CardTitle>
        <CardDescription>
          Case as que são a mesma. O que sobrar sem vizinho é fachada — e é a face de fora que leva
          revestimento externo. {abertas} ainda {abertas === 1 ? 'dá' : 'dão'} para a rua.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {draft.walls.length === 0 ? (
          <EmptyState title="Sem paredes" description="Elas nascem junto com o ambiente." />
        ) : (
          grupos.map(({ room, open: livres, paired: casadas }) => {
            const mostrando = comCasadas.has(room.id);
            const visiveis = mostrando ? [...livres, ...casadas] : livres;

            return (
              <section key={room.id} className="flex flex-col gap-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold">{room.name}</h3>
                  <div className="flex items-center gap-2 text-xs text-(--color-content-muted)">
                    <span>
                      {livres.length} {livres.length === 1 ? 'aberta' : 'abertas'}
                      {casadas.length > 0 &&
                        ` · ${String(casadas.length)} casada${casadas.length === 1 ? '' : 's'}`}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => dispatch({ type: 'ADD_WALL', roomId: room.id })}
                    >
                      <Plus className="size-4" /> Parede
                    </Button>
                    {casadas.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-expanded={mostrando}
                        onClick={() =>
                          setComCasadas((atual) => {
                            const proximo = new Set(atual);
                            if (proximo.has(room.id)) proximo.delete(room.id);
                            else proximo.add(room.id);
                            return proximo;
                          })
                        }
                      >
                        {mostrando ? (
                          <>
                            <ChevronUp className="size-4" /> Ocultar casadas
                          </>
                        ) : (
                          <>
                            <ChevronDown className="size-4" /> Mostrar casadas
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {visiveis.length === 0 ? (
                  <p className="rounded-md border border-dashed border-(--color-border-subtle) px-3 py-2 text-xs text-(--color-content-muted)">
                    Todas as paredes deste ambiente já estão casadas.
                  </p>
                ) : (
                  visiveis.map((wall) => (
                    <ParedeRow
                      key={wall.id}
                      wall={wall}
                      workspace={workspace}
                      draft={draft}
                      dispatch={dispatch}
                      onCasou={() => setComCasadas((atual) => new Set(atual).add(room.id))}
                    />
                  ))
                )}
              </section>
            );
          })
        )}

        {soltos.length > 0 && (
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Pilares e vigas sem parede</h3>
            <ul className="flex flex-col gap-1">
              {soltos.map((element) => (
                <ElementoSoltoRow key={element.id} element={element} dispatch={dispatch} />
              ))}
            </ul>
          </section>
        )}
      </CardContent>
    </Card>
  );
}

function ParedeRow({
  wall,
  workspace,
  draft,
  dispatch,
  onCasou,
}: {
  wall: Wall;
  workspace: WorkspaceDraft;
  draft: TypologyDraft;
  dispatch: Dispatch;
  /**
   * Chamado quando esta parede acabou de casar.
   *
   * Serve para **abrir** o grupo: conjugar é a decisão logo seguinte a casar, e recolher a
   * parede no instante em que ela ganha vizinho esconderia o botão justamente de quem
   * acabou de precisar dele.
   */
  onCasou: () => void;
}) {
  const candidatas = pairableWith(draft, wall);
  const deFora = pairableAcrossTypologies(workspace, wall);
  // A divisa é do pavimento: os vãos dela moram na bancada, não na tipologia.
  const daBancada = wall.owner.kind === 'FLOOR_PLAN';
  const vaos = (daBancada ? (workspace.partyOpenings ?? []) : draft.openings).filter(
    (opening) => opening.wallId === wall.id,
  );
  const casada = wall.sideB !== undefined;
  // O trecho é o encontro do elemento com esta parede; o elemento mora ao lado dela — na
  // tipologia quando a parede é dela, na bancada quando a parede é divisa. As duas listas
  // se leem juntas porque a parede só está em uma delas, e a viga pode cruzar as duas.
  const todasAsParedes = [...draft.walls, ...partyWallsTouching(workspace, draft)];
  const paredesPorId = new Map(todasAsParedes.map((candidata) => [candidata.id, candidata]));
  const todosOsElementos = [...draft.elements, ...(workspace.partyElements ?? [])];
  const todosOsTrechos = [...draft.elementSegments, ...(workspace.partyElementSegments ?? [])];
  const estrutura = todosOsTrechos
    .filter((segment) => segment.wallId === wall.id)
    .flatMap((segment) => {
      const element = todosOsElementos.find((candidate) => candidate.id === segment.elementId);
      if (element === undefined) return [];
      // A corrida que vale, e por onde ela passa: na viga é a soma dos trechos (fatia 4).
      const trechos = todosOsTrechos.filter((candidato) => candidato.elementId === element.id);
      const naoCruza = todasAsParedes.filter(
        (candidata) => !trechos.some((trecho) => trecho.wallId === candidata.id),
      );
      const run = elementRun(element, trechos, paredesPorId);
      return [
        {
          segment,
          element,
          trechos,
          naoCruza,
          run,
          // A espala é do **elemento** (RN-190): a linha mostra o metro da peça inteira,
          // e não o pedaço que esta parede vê. Somar por parede daria ao pilar de canto
          // duas corridas onde ele tem uma.
          espala: elementEspala(element, trechos, paredesPorId, run),
        },
      ];
    });

  return (
    <div className="flex flex-col gap-2 border-b border-(--color-border-subtle) py-2 last:border-b-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <NomeInput
            aria-label="Nome da parede"
            value={describeWall(wallLabelOf(workspace, draft, wall.id))}
            onChange={(name) => dispatch({ type: 'RENAME_WALL', wallId: wall.id, name })}
            className="h-8 w-64 border-transparent bg-transparent px-1 text-sm font-medium hover:border-(--color-border-subtle) focus:border-(--color-border-subtle) md:h-8"
          />
          {casada && <Badge tone="info">casada</Badge>}
          {daBancada && <Badge tone="warn">divisa com outra tipologia</Badge>}
          {casada && !wall.joined && (
            <span className="text-xs text-(--color-content-muted)">
              confira o comprimento — cada lado foi medido no seu ambiente
            </span>
          )}
          {wall.joined && <Badge tone="warn">conjugada — sem parede</Badge>}
          {!casada && <Badge tone="neutral">fachada</Badge>}
        </div>

        <div className="flex items-center gap-2">
          <Label htmlFor={`comp-${wall.id}`} className="text-xs">
            m
          </Label>
          <NumeroInput
            id={`comp-${wall.id}`}
            value={wall.length}
            onChange={(length) =>
              dispatch({ type: 'UPDATE_WALL', wallId: wall.id, patch: { length } })
            }
            className="w-20"
          />

          {casada ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => dispatch({ type: 'TOGGLE_JOIN', wallId: wall.id })}
              >
                {wall.joined ? 'Separar' : 'Conjugar'}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Desfazer casamento"
                onClick={() => dispatch({ type: 'UNMERGE_WALL', wallId: wall.id })}
              >
                <Link2Off className="size-4" />
              </Button>
            </>
          ) : (
            <CasarSelect
              wallId={wall.id}
              candidatas={candidatas}
              deFora={deFora}
              draft={draft}
              dispatch={dispatch}
              onCasou={onCasou}
            />
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={() => dispatch({ type: 'ADD_OPENING', wallId: wall.id, kind: 'PORTA' })}
          >
            <DoorOpen className="size-4" /> Vão
          </Button>

          {/*
            Vale na divisa também: a viga que corre em cima dela é uma só, e o concreto se
            reparte entre os dois aptos como a alvenaria (RN-163). Cadastrá-la de um lado
            só deixaria o vizinho cobrando bloco onde há concreto.
          */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dispatch({ type: 'ADD_ELEMENT', wallId: wall.id, kind: 'PILAR' })}
          >
            <Columns3 className="size-4" /> Pilar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dispatch({ type: 'ADD_ELEMENT', wallId: wall.id, kind: 'VIGA' })}
          >
            <RectangleHorizontal className="size-4" /> Viga
          </Button>
        </div>
      </div>

      {vaos.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {vaos.map((vao) => (
            <li key={vao.id} className="flex items-center gap-1 text-xs">
              <select
                value={vao.kind}
                aria-label="Tipo do vão"
                onChange={(event) =>
                  dispatch({
                    type: 'UPDATE_OPENING',
                    openingId: vao.id,
                    patch: { kind: event.target.value as OpeningKind },
                  })
                }
                className={SELECT_CLASS}
              >
                {Object.entries(OPENING_KIND_LABEL).map(([value, rotulo]) => (
                  <option key={value} value={value}>
                    {rotulo}
                  </option>
                ))}
              </select>
              <MedidaVao
                id={`v-l-${vao.id}`}
                value={vao.width}
                onChange={(width) =>
                  dispatch({ type: 'UPDATE_OPENING', openingId: vao.id, patch: { width } })
                }
              />
              <span aria-hidden>×</span>
              <MedidaVao
                id={`v-a-${vao.id}`}
                value={vao.height}
                onChange={(height) =>
                  dispatch({ type: 'UPDATE_OPENING', openingId: vao.id, patch: { height } })
                }
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remover vão"
                onClick={() => dispatch({ type: 'REMOVE_OPENING', openingId: vao.id })}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {estrutura.length > 0 && (
        <ul className="flex flex-col gap-1">
          {estrutura.map(({ element, segment, trechos, naoCruza, run, espala }) => (
            <EstruturaRow
              key={segment.id}
              element={element}
              segment={segment}
              wall={wall}
              trechos={trechos.length}
              naoCruza={naoCruza}
              run={run}
              espala={espala}
              draft={draft}
              ladoA={nomeDoLado(draft, wall.sideA.roomId)}
              ladoB={wall.sideB === undefined ? 'a rua' : nomeDoLado(draft, wall.sideB.roomId)}
              peDireito={peDireitoDosLados(workspace, wall)}
              dispatch={dispatch}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Um pilar ou uma viga nesta parede.
 *
 * Mostra os três números que a conta usa — a seção, a corrida e como o elemento encontra a
 * parede — e diz, ao lado, **quanto de bloco isto tirou**. O desconto na tela é o ponto:
 * ele é o número que a fase inteira existe para corrigir, e um total que muda sem
 * explicação é um total em que ninguém confia.
 */
function EstruturaRow({
  element,
  segment,
  wall,
  ladoA,
  ladoB,
  peDireito,
  trechos,
  naoCruza,
  run,
  espala,
  draft,
  dispatch,
}: {
  element: StructuralElement;
  segment: ElementSegment;
  wall: Wall;
  /** O nome de cada lado da parede — na fachada, o lado B é a rua. */
  ladoA: string;
  ladoB: string;
  /** O pé-direito de cada lado: é ele que mede a face, e não a altura da parede (RN-162). */
  peDireito: { readonly A: number; readonly B: number };
  /** Quantas paredes este elemento cruza — a viga que atravessa cruza mais de uma. */
  trechos: number;
  /** As paredes que ele ainda não cruza, para a viga poder seguir em frente. */
  naoCruza: readonly Wall[];
  /** A corrida que vale: a altura do pilar, a soma dos trechos da viga (fatia 4). */
  run: number;
  /** O metro linear de espala do elemento inteiro, somando todos os trechos (RN-187). */
  espala: number;
  draft: TypologyDraft;
  dispatch: Dispatch;
}) {
  const ehPilar = element.kind === 'PILAR';
  const sombra = segmentShadowArea(element, segment, wall);
  // O avanço que a conta usa — digitado, ou derivado da seção menos a espessura da parede.
  // A tela mostra **o mesmo número** que o total, e diz de onde ele veio: mostrar zero
  // enquanto a conta usa 2,5 cm é como uma tela começa a discordar do total.
  const {
    advance: avanco,
    side: ladoDoAvanco,
    derived: avancoDerivado,
  } = segmentAdvance(element, segment, wall);
  // Peça maior que o plano em que ela está. Não se corta a conta — estrutura maior que a
  // parede é erro de digitação, e o lugar de dizê-lo é aqui, antes que a alvenaria zere
  // sem que ninguém entenda por quê.
  //
  // Na conjugada, não: ali não há parede (RN-161), e "maior que a parede" não diz nada
  // sobre um plano que não existe.
  const passaDaParede = !wall.joined && sombra > wallGrossArea(wall);
  // A viga com trecho em parede que mede algo tem a corrida **calculada**, e o número
  // digitado deixa de valer. Mostrá-lo como campo editável seria oferecer uma correção
  // que a conta ignora — e é assim que uma tela começa a discordar do total.
  const corridaCalculada = !ehPilar && run !== element.length;
  // Só a viga paralela mede trecho: a perpendicular deixa a seção, e o pilar corre para
  // cima. Ausente é a parede inteira, e o campo mostra isso em vez de fingir um número.
  const temTrecho = !ehPilar && segment.orientation === 'PARALELA';

  return (
    <li className="flex flex-wrap items-center gap-2 text-xs">
      <Badge tone="neutral">{STRUCTURAL_ELEMENT_KIND_LABEL[element.kind]}</Badge>
      <span className="sr-only">{descricaoDoElemento(element)}</span>

      <SecaoDoElemento element={element} dispatch={dispatch} />

      <Label htmlFor={`e-c-${element.id}`} className="text-xs text-(--color-content-muted)">
        {ehPilar ? 'altura' : 'comprimento'}
      </Label>
      {corridaCalculada ? (
        <span
          className="font-medium"
          title={`Soma dos trechos${
            (element.freeSpan ?? 0) > 0 ? ' e do vão livre' : ''
          } — corrigir o comprimento da parede corrige a viga.`}
        >
          {metros(run)} m
        </span>
      ) : (
        <MedidaEstrutura
          id={`e-c-${element.id}`}
          rotulo={ehPilar ? 'Altura do pilar' : 'Comprimento da viga'}
          value={element.length}
          onChange={(length) =>
            dispatch({ type: 'UPDATE_ELEMENT', elementId: element.id, patch: { length } })
          }
        />
      )}

      <select
        value={segment.orientation}
        aria-label="Como encontra a parede"
        onChange={(event) =>
          dispatch({
            type: 'UPDATE_ELEMENT_SEGMENT',
            segmentId: segment.id,
            patch: { orientation: event.target.value as ElementOrientation },
          })
        }
        className={SELECT_CLASS}
      >
        {ELEMENT_ORIENTATIONS.map((orientation) => (
          <option key={orientation} value={orientation}>
            {orientationLabel(element.kind, orientation)}
          </option>
        ))}
      </select>

      {temTrecho && (
        <>
          <Label htmlFor={`e-t-${element.id}`} className="text-xs text-(--color-content-muted)">
            trecho
          </Label>
          <MedidaEstrutura
            id={`e-t-${element.id}`}
            rotulo="Trecho nesta parede"
            value={segment.length ?? wall.length}
            onChange={(length) =>
              dispatch({ type: 'UPDATE_ELEMENT_SEGMENT', segmentId: segment.id, patch: { length } })
            }
          />
        </>
      )}

      {!ehPilar && (
        <>
          <Label htmlFor={`e-v-${element.id}`} className="text-xs text-(--color-content-muted)">
            vão livre
          </Label>
          <MedidaEstrutura
            id={`e-v-${element.id}`}
            rotulo="Vão livre — o trecho que não cruza parede alguma"
            value={element.freeSpan ?? 0}
            onChange={(freeSpan) =>
              dispatch({ type: 'UPDATE_ELEMENT', elementId: element.id, patch: { freeSpan } })
            }
          />
        </>
      )}

      {/*
        A mesma viga em outra parede — e não uma viga nova. A seção é uma só, e o
        comprimento passa a ser a soma dos trechos: cadastrar duas seria manter duas
        seções iguais na mão, com um dia em que só uma foi corrigida.
      */}
      {!ehPilar && naoCruza.length > 0 && (
        <select
          value=""
          aria-label="Esta viga também cruza"
          onChange={(event) => {
            if (event.target.value === '') return;
            dispatch({
              type: 'ADD_ELEMENT_SEGMENT',
              elementId: element.id,
              wallId: event.target.value as WallId,
            });
          }}
          className={SELECT_CLASS}
        >
          <option value="">+ cruza também…</option>
          {naoCruza.map((candidata) => (
            <option key={candidata.id} value={candidata.id}>
              {wallShortName(draft.labels[candidata.id])}
            </option>
          ))}
        </select>
      )}

      {trechos > 1 && <Badge tone="info">{trechos} paredes</Badge>}

      <Label htmlFor={`e-a-${element.id}`} className="text-xs text-(--color-content-muted)">
        avança
      </Label>
      <MedidaEstrutura
        id={`e-a-${element.id}`}
        rotulo="Quanto sobra além da face da parede"
        value={avanco}
        onChange={(advance) =>
          dispatch({ type: 'UPDATE_ELEMENT_SEGMENT', segmentId: segment.id, patch: { advance } })
        }
      />

      {/*
        Dizer que o número foi **derivado** é o que torna a correção possível: sem isso,
        quem confere não sabe se aquele 0,03 foi alguém que mediu ou a subtração da
        espessura da parede — e digitar por cima parece mexer no que já estava certo.
      */}
      {avancoDerivado && (
        <span
          className="text-(--color-content-muted)"
          title={`A peça atravessa ${metros(thicknessAcrossWall(element, segment) ?? 0)} m e a parede tem ${metros(wall.thickness ?? 0)} m. Digite por cima se mediu outra coisa.`}
        >
          da seção
        </span>
      )}

      {/*
        Para onde ele sobra só importa depois que sobra alguma coisa. Rente, a pergunta
        não tem resposta — e um seletor pedindo lado para um pilar que não avança seria
        pedir uma decisão que não muda número nenhum.
      */}
      {avanco > 0 && (
        <select
          value={ladoDoAvanco}
          aria-label="Para que lado avança"
          onChange={(event) =>
            dispatch({
              type: 'UPDATE_ELEMENT_SEGMENT',
              segmentId: segment.id,
              patch: { advanceSide: event.target.value as AdvanceSide },
            })
          }
          className={SELECT_CLASS}
        >
          <option value="A">para {ladoA}</option>
          <option value="B">para {ladoB}</option>
          <option value="AMBOS">para os dois</option>
        </select>
      )}

      <span className="text-(--color-content-muted)">
        −{metros(sombra)} m² de bloco
        {avanco > 0 &&
          ` · +${metros(revestimentoDoAvanco(element, segment, wall, peDireito))} m² de reboco`}
        {/*
          A espala é do elemento, e por isso vem sem o sinal de mais: ela não se soma linha
          a linha como o bloco e o reboco desta parede. O pilar de canto mostra o mesmo
          número nas duas linhas dele, que é o certo — é uma peça só.
        */}
        {espala > 0 && ` · ${metros(espala)} m de espala`}
      </span>

      {passaDaParede && (
        <Badge tone="danger">
          maior que a parede — {metros(sombra)} m² num plano de {metros(wallGrossArea(wall))} m²
        </Badge>
      )}

      {/*
        Na divisa o concreto não é todo desta tipologia. Dizer isso na linha evita a
        pergunta seguinte — "por que o volume subiu menos do que a peça que eu cadastrei?"
      */}
      {wall.owner.kind === 'FLOOR_PLAN' && <Badge tone="warn">concreto repartido</Badge>}

      {/*
        Tira o **trecho**, e não o elemento: a viga que cruza três paredes continua
        existindo quando sai de uma delas. Saindo da última, ela vai junto — concreto que
        não encosta em parede nenhuma somaria volume que ninguém vê na tela.
      */}
      <Button
        variant="ghost"
        size="icon"
        aria-label={
          trechos > 1
            ? 'Tirar desta parede'
            : `Remover ${STRUCTURAL_ELEMENT_KIND_LABEL[element.kind].toLocaleLowerCase('pt-BR')}`
        }
        onClick={() => dispatch({ type: 'REMOVE_ELEMENT_SEGMENT', segmentId: segment.id })}
      >
        <Trash2 className="size-4" />
      </Button>
    </li>
  );
}

/**
 * O reboco que o avanço criou, para a linha dizer o que mudou.
 *
 * Sai da mesma conta da geometria — desconta e devolve —, e o que sobra da subtração é
 * exatamente a faixa nova. Repetir a fórmula aqui daria à tela um número que a conta não
 * conhece, e é assim que uma tela começa a discordar do total.
 */
function revestimentoDoAvanco(
  element: StructuralElement,
  segment: ElementSegment,
  wall: Wall,
  peDireito: { readonly A: number; readonly B: number },
): number {
  // O lado sai de `segmentAdvance`, e não de `advanceSide` cru: o avanço derivado nasce
  // **centrado**, e ler o campo em branco como "lado A" mostraria aqui metade do reboco
  // que o total já contou. É assim que uma tela começa a discordar do total.
  const { side: lado } = segmentAdvance(element, segment, wall);
  const lados: readonly ('A' | 'B')[] = lado === 'AMBOS' ? ['A', 'B'] : [lado];

  // A altura é a de **cada lado**, e não a da parede: a face mede até o forro (RN-162), e
  // usar os 2,90 da laje mostraria na linha um número que o total não tem. Avançando para
  // os dois, os dois entram — é o que a conta soma, e é o que a pessoa vai conferir.
  return lados.reduce((total, qual) => {
    const { discount, given } = segmentFaceArea(element, segment, wall, qual, peDireito[qual]);
    return total + (given - discount);
  }, 0);
}

/**
 * O nome do elemento para quem lê a tela: a seção em centímetros, ou "polígono" quando o
 * contorno é que manda na forma (06/10/2026) — a seção digitada não diz o que a peça é.
 */
function descricaoDoElemento(element: StructuralElement): string {
  if (element.outline === undefined) return describeSection(element);
  const nome = element.name ?? STRUCTURAL_ELEMENT_KIND_LABEL[element.kind];
  return `${nome} polígono`;
}

/**
 * A seção do pilar ou da viga, editável. O pilar com contorno não tem largura × profundidade
 * a digitar — a forma é o contorno, e a seção guardada só o envolve —, então mostra a área
 * que a conta usa. A viga com contorno segue com a largura e a altura: a largura diz quanto
 * do contorno é ponta, e a altura é a do volume.
 */
function SecaoDoElemento({
  element,
  dispatch,
}: {
  element: StructuralElement;
  dispatch: Dispatch;
}) {
  const ehPilar = element.kind === 'PILAR';
  if (ehPilar && element.outline !== undefined) {
    return (
      <span
        className="font-medium"
        title="O contorno traçado na planta manda na forma do pilar. Para mudar, trace de novo."
      >
        polígono · {metros(polygonArea(element.outline))} m²
      </span>
    );
  }
  return (
    <>
      <MedidaEstrutura
        id={`e-l-${element.id}`}
        rotulo={ehPilar ? 'Largura do pilar' : 'Largura da viga'}
        value={element.sectionWidth}
        onChange={(sectionWidth) =>
          dispatch({ type: 'UPDATE_ELEMENT', elementId: element.id, patch: { sectionWidth } })
        }
      />
      <span aria-hidden>×</span>
      <MedidaEstrutura
        id={`e-p-${element.id}`}
        rotulo={ehPilar ? 'Profundidade do pilar' : 'Altura da viga'}
        value={element.sectionDepth}
        onChange={(sectionDepth) =>
          dispatch({ type: 'UPDATE_ELEMENT', elementId: element.id, patch: { sectionDepth } })
        }
      />
      {element.outline !== undefined && <Badge tone="info">polígono</Badge>}
    </>
  );
}

/**
 * Um pilar ou uma viga que não encosta em parede nenhuma (06/10/2026): vive pelo contorno
 * que foi traçado. Sem o trecho não há "bloco que ele tirou" para mostrar — só o que ele
 * mede e o botão de tirá-lo do desenho.
 */
function ElementoSoltoRow({
  element,
  dispatch,
}: {
  element: StructuralElement;
  dispatch: Dispatch;
}) {
  const ehPilar = element.kind === 'PILAR';
  const rotulo = STRUCTURAL_ELEMENT_KIND_LABEL[element.kind];
  const corrida = elementRun(element, [], new Map());
  return (
    <li className="flex flex-wrap items-center gap-2 text-xs">
      <Badge tone="neutral">{rotulo}</Badge>
      <Badge tone="warn">sem parede</Badge>
      <span className="sr-only">{descricaoDoElemento(element)}</span>
      <SecaoDoElemento element={element} dispatch={dispatch} />
      <Label htmlFor={`e-c-${element.id}`} className="text-xs text-(--color-content-muted)">
        {ehPilar ? 'altura' : 'comprimento'}
      </Label>
      {ehPilar ? (
        <MedidaEstrutura
          id={`e-c-${element.id}`}
          rotulo="Altura do pilar"
          value={element.length}
          onChange={(length) =>
            dispatch({ type: 'UPDATE_ELEMENT', elementId: element.id, patch: { length } })
          }
        />
      ) : (
        <span
          className="font-medium"
          title="Sai do contorno: o perímetro sem as duas pontas, dividido por dois."
        >
          {metros(corrida)} m
        </span>
      )}
      <Button
        variant="ghost"
        size="icon"
        aria-label={`Remover ${rotulo.toLocaleLowerCase('pt-BR')}`}
        onClick={() => dispatch({ type: 'REMOVE_ELEMENT', elementId: element.id })}
      >
        <Trash2 className="size-4" />
      </Button>
    </li>
  );
}

function MedidaEstrutura({
  id,
  rotulo,
  value,
  onChange,
}: {
  id: string;
  rotulo: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <NumeroInput id={id} aria-label={rotulo} value={value} onChange={onChange} className="w-16" />
  );
}

function MedidaVao({
  id,
  value,
  onChange,
}: {
  id: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <NumeroInput
      id={id}
      aria-label="Medida do vão"
      value={value}
      onChange={onChange}
      className="w-16"
    />
  );
}

/**
 * Com quem esta parede casa.
 *
 * Duas listas no mesmo `select`, e a separação importa: casar dentro da tipologia junta
 * dois ambientes do mesmo apartamento; casar com outra **cria uma divisa do pavimento**,
 * que se reparte meio a meio entre as duas (RN-163). São consequências diferentes, e
 * misturá-las numa lista chapada esconderia isso de quem escolhe.
 */
function CasarSelect({
  wallId,
  candidatas,
  deFora,
  draft,
  dispatch,
  onCasou,
}: {
  wallId: WallId;
  candidatas: readonly { id: WallId }[];
  deFora: readonly CandidataDeFora[];
  draft: TypologyDraft;
  dispatch: Dispatch;
  onCasou: () => void;
}) {
  return (
    <select
      value=""
      aria-label="Casar com"
      className={SELECT_CLASS}
      onChange={(event) => {
        if (event.target.value === '') return;
        dispatch({
          type: 'MERGE_WALLS',
          keepId: wallId,
          dropId: event.target.value as WallId,
        });
        onCasou();
      }}
    >
      <option value="">
        {candidatas.length === 0 && deFora.length === 0 ? 'Sem parede livre' : 'Casar com…'}
      </option>
      {candidatas.length > 0 && (
        <optgroup label={`Dentro de ${draft.name}`}>
          {candidatas.map((candidata) => (
            <option key={candidata.id} value={candidata.id}>
              {wallShortName(draft.labels[candidata.id])}
            </option>
          ))}
        </optgroup>
      )}
      {deFora.length > 0 && (
        <optgroup label="Divisa com outra tipologia">
          {deFora.map(({ wall, typology }) => (
            <option key={wall.id} value={wall.id}>
              {typology.name} · {wallShortName(typology.labels[wall.id])}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

// ── Resultado ───────────────────────────────────────────────────────────────────

/** Pilar e viga, na ordem do cadastro — cada um na sua linha (RN-257). */
const PECAS = Object.keys(STRUCTURAL_ELEMENT_KIND_LABEL) as readonly StructuralElementKind[];

/** "3,00 m · 3,30 m² · 1 un" — o guarda-corpo como se compra (RN-268). */
function medidasDoGuardaCorpo(railing: ReturnType<typeof summarizeGeometry>['railing']): string {
  return `${metros(railing.length)} m · ${metros(railing.area)} m² · ${railing.count.toLocaleString('pt-BR')} un`;
}

function ResultadoCard({
  workspace,
  draft,
  tipologia,
  predio,
}: {
  workspace: WorkspaceDraft;
  draft: TypologyDraft;
  tipologia: ReturnType<typeof summarizeGeometry>;
  predio: ReturnType<typeof summarizeGeometry>;
}) {
  const conjugadas = draft.walls.filter((wall) => wall.joined).length;
  const banheiros = draft.rooms.filter((room) => room.kind === 'BANHEIRO').length;
  // As peças da planilha por peça (Fase 28): contam pela quantidade pronta, e não pelo desenho.
  const importadas = pecasImportadas(draft.pieceQuantities).size;
  // A viga da divisa aparece nas duas tipologias, e conta nas duas: o concreto dela é que
  // se reparte, não a peça.
  const pecas = structureOf(workspace, draft).elements.length;
  const doPavimento = floorsOfDrawing(workspace, draft);
  const vezes = floorsOf(workspace, draft);
  const nomeDoPavimento = FLOOR_PLAN_KIND_LABEL[draft.floorKind];

  return (
    <Card
      // Gruda logo abaixo da faixa do quantitativo, que também é presa no topo: com `top-4`
      // o título passava por baixo dela e sumia ao rolar.
      className="order-first h-fit lg:order-none lg:sticky lg:top-[calc(var(--altura-da-faixa,0px)+1rem)]"
    >
      <CardHeader>
        <CardTitle>O que sai de {draft.name}</CardTitle>
        <CardDescription>
          Alvenaria conta a parede uma vez; revestimento conta as duas faces.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <dl className="flex flex-col gap-2 text-sm">
          <Linha rotulo="Alvenaria" valor={`${metros(tipologia.wallArea)} m²`} />
          <Linha rotulo="Revestimento de parede" valor={`${metros(tipologia.faceArea)} m²`} />
          <Linha rotulo="Fachada" valor={`${metros(tipologia.outsideFaceArea)} m²`} />
          <Linha rotulo="Piso" valor={`${metros(tipologia.floorArea)} m²`} />
          <Linha rotulo="Teto" valor={`${metros(tipologia.ceilingArea)} m²`} />
          {/*
            O concreto só aparece quando existe. Uma linha de "0,000 m³" em toda tipologia
            sem estrutura diria que o desenho respondeu "nenhum" — e ele não respondeu
            nada. Três casas porque o metro cúbico é pequeno e a terceira é onde ele mora.
          */}
          {/*
            Uma linha por peça, e nunca a soma (RN-257): é o número que o serviço de
            concretagem de pilar recebe, e ele não recebe o das vigas.
          */}
          {PECAS.map(
            (peca) =>
              tipologia.volumeByKind[peca] > 0 && (
                <Linha
                  key={`concreto-${peca}`}
                  rotulo={`Concreto de ${STRUCTURAL_ELEMENT_KIND_LABEL[peca].toLocaleLowerCase('pt-BR')}`}
                  valor={`${metros(tipologia.volumeByKind[peca], 3)} m³`}
                />
              ),
          )}
          {/*
            Mesma regra do concreto: só aparece quando existe. Espala zero em toda
            tipologia sem estrutura diria que o desenho respondeu "nenhuma", e ele não
            respondeu nada.
          */}
          {PECAS.map(
            (peca) =>
              tipologia.espalaByKind[peca] > 0 && (
                <Linha
                  key={`espala-${peca}`}
                  rotulo={`Espala de ${STRUCTURAL_ELEMENT_KIND_LABEL[peca].toLocaleLowerCase('pt-BR')}`}
                  valor={`${metros(tipologia.espalaByKind[peca])} m`}
                />
              ),
          )}
          {/*
            O guarda-corpo, pela mesma regra: só aparece quando existe. As três medidas numa
            linha, porque é a mesma peça comprada de três jeitos (D25).
          */}
          {tipologia.railing.count > 0 && (
            <Linha rotulo="Guarda-corpo" valor={medidasDoGuardaCorpo(tipologia.railing)} />
          )}
          {/*
            O shaft também só aparece quando existe, e numa linha só: o buraco, o requadro e
            quantos. A área dele já saiu do piso e do teto acima (RN-271).
          */}
          {tipologia.shaft.count > 0 && (
            <Linha
              rotulo={SHAFT_LABEL}
              valor={`${metros(tipologia.shaft.area)} m² · ${metros(tipologia.shaft.contour)} m · ${String(tipologia.shaft.count)} un`}
            />
          )}
          {/*
            As peças que vieram da planilha por peça (Fase 28, RN-384): a quantidade delas é a da
            planilha, e a peça desenhada com o mesmo nome sai das medidas do desenho — dizer
            quantas são é o que explica por que o bloco traçado não aparece no concreto.
          */}
          {importadas > 0 && (
            <Linha
              rotulo="Da planilha por peça"
              valor={contar(importadas, 'peça pronta', 'peças prontas')}
            />
          )}
        </dl>

        {/*
          O multiplicador **não se digita**: ele é quantos pavimentos tipo a obra tem, e
          quem sabe isso é a estrutura. Digitado, ele envelhecia calado — a estrutura
          passava a doze e o número continuava no vinte com que a tipologia nasceu.
        */}
        <div className="flex flex-col gap-1 border-t border-(--color-border-subtle) pt-4">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-(--color-content-muted)">
              {vezes === 1 ? 'Acontece' : `${nomeDoPavimento} na obra`}
            </span>
            <span className="font-medium">
              {vezes} {vezes === 1 ? 'vez' : 'pavimentos'}
            </span>
          </div>
          <p className="text-xs text-(--color-content-muted)">
            {draft.floors !== undefined
              ? 'Esta tipologia tem número próprio e não acompanha a estrutura.'
              : doPavimento === 0
                ? `Nenhum ${nomeDoPavimento.toLocaleLowerCase('pt-BR')} cadastrado ainda — o total mostra o desenho uma vez. Cadastre os pavimentos na Estrutura.`
                : `Sai da Estrutura: ${String(doPavimento)} ${doPavimento === 1 ? 'pavimento' : 'pavimentos'} do tipo ${nomeDoPavimento} na obra.`}
          </p>
        </div>

        <dl className="flex flex-col gap-2 text-sm">
          <Linha rotulo="Alvenaria no prédio" valor={`${metros(predio.wallArea)} m²`} destaque />
          <Linha rotulo="Revestimento no prédio" valor={`${metros(predio.faceArea)} m²`} destaque />
          {PECAS.map(
            (peca) =>
              predio.volumeByKind[peca] > 0 && (
                <Linha
                  key={`concreto-${peca}`}
                  rotulo={`Concreto de ${STRUCTURAL_ELEMENT_KIND_LABEL[peca].toLocaleLowerCase('pt-BR')} no prédio`}
                  valor={`${metros(predio.volumeByKind[peca], 3)} m³`}
                  destaque
                />
              ),
          )}
          {PECAS.map(
            (peca) =>
              predio.espalaByKind[peca] > 0 && (
                <Linha
                  key={`espala-${peca}`}
                  rotulo={`Espala de ${STRUCTURAL_ELEMENT_KIND_LABEL[peca].toLocaleLowerCase('pt-BR')} no prédio`}
                  valor={`${metros(predio.espalaByKind[peca])} m`}
                  destaque
                />
              ),
          )}
          {predio.railing.count > 0 && (
            <Linha
              rotulo="Guarda-corpo no prédio"
              valor={medidasDoGuardaCorpo(predio.railing)}
              destaque
            />
          )}
        </dl>

        <p className="text-xs text-(--color-content-muted)">
          {draft.rooms.length} {draft.rooms.length === 1 ? 'ambiente' : 'ambientes'} ·{' '}
          {draft.walls.length} {draft.walls.length === 1 ? 'parede' : 'paredes'}
          {conjugadas > 0 && ` · ${String(conjugadas)} conjugada${conjugadas === 1 ? '' : 's'}`}
          {banheiros > 0 && ` · banheiro não conjuga`}
        </p>

        {pecas > 0 && (
          <p className="text-xs text-(--color-content-muted)">
            {pecas} {pecas === 1 ? 'peça de estrutura' : 'peças de estrutura'} no desenho: a
            alvenaria acima já está sem o bloco que o concreto ocupa.
          </p>
        )}

        {draft.rooms.length > 0 && draft.walls.every((wall) => wall.sideB === undefined) && (
          <p className="flex items-start gap-2 text-xs text-(--color-content-muted)">
            <Link2 className="mt-0.5 size-4 shrink-0" />
            Nenhuma parede casada ainda: enquanto isso, toda parede é fachada e a alvenaria está
            contada duas vezes entre os ambientes vizinhos.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Linha({
  rotulo,
  valor,
  destaque = false,
}: {
  rotulo: string;
  valor: string;
  destaque?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-(--color-content-muted)">{rotulo}</dt>
      <dd className={destaque ? 'font-semibold' : 'font-medium'}>{valor}</dd>
    </div>
  );
}

/** `1 parede casada` · `3 paredes casadas`. */
function contar(count: number, singular: string, many: string): string {
  return `${String(count)} ${count === 1 ? singular : many}`;
}

/**
 * As etapas da montagem, na ordem em que acontecem — e é uma ordem de verdade: sem
 * pavimento ligado a um desenho não há onde a tipologia acontecer, sem ambiente não há
 * parede, sem serviço não há o que medir contra o traçado. O número diz isso; o estado
 * ao lado diz onde a bancada está; e cada etapa leva ao cartão dela.
 *
 * Etapa cheia é a que já tem conteúdo, não a que está "certa": a bancada não julga o
 * desenho, só diz o que ainda está em branco.
 */
function EtapasDaMontagem({
  estrutura,
  espinha,
  workspace,
  draft,
  escolhidos,
}: {
  estrutura: ProjectStructureView;
  espinha: ProjectTakeoff;
  workspace: WorkspaceDraft;
  draft: TypologyDraft;
  /** Serviços escolhidos na tipologia aberta — é o que o orçamento e os itens mostram. */
  escolhidos: number;
}) {
  const ligados = estrutura.tree.towers
    .flatMap((torre) => torre.floors)
    .filter((node) => node.floor.floorPlanId !== undefined).length;
  const tipologias = espinha.unitTypes.length;
  const comEstrutura = hasStructure(workspace);
  const casadas = draft.walls.filter((wall) => wall.sideB !== undefined).length;
  // Pilar e viga moram na parede, e por isso contam na etapa dela: uma etapa própria
  // sugeriria uma tela própria, e a estrutura se cadastra no cartão da parede.
  const pecas = structureOf(workspace, draft).elements.length;
  const servicos = escolhidos;

  const etapas: readonly {
    id: string;
    nome: string;
    estado: string;
    cheia: boolean;
    disponivel: boolean;
  }[] = [
    {
      id: 'etapa-pavimentos',
      nome: 'Pavimentos',
      estado:
        ligados === 0
          ? 'nenhum ligado a um desenho'
          : contar(ligados, 'pavimento ligado', 'pavimentos ligados'),
      cheia: ligados > 0,
      disponivel: true,
    },
    {
      id: 'etapa-tipologias',
      nome: 'Tipologias',
      estado:
        tipologias === 0 ? 'nenhuma cadastrada' : contar(tipologias, 'cadastrada', 'cadastradas'),
      cheia: tipologias > 0,
      disponivel: true,
    },
    {
      id: 'etapa-planta',
      nome: 'Planta',
      estado: workspace.plan === undefined ? 'sem prancha · opcional' : 'prancha carregada',
      cheia: workspace.plan !== undefined,
      disponivel: comEstrutura,
    },
    {
      id: 'etapa-ambientes',
      nome: 'Ambientes',
      estado:
        draft.rooms.length === 0
          ? `nenhum em ${draft.name}`
          : `${contar(draft.rooms.length, 'ambiente', 'ambientes')} em ${draft.name}`,
      cheia: draft.rooms.length > 0,
      disponivel: comEstrutura,
    },
    {
      id: 'etapa-paredes',
      nome: 'Paredes',
      estado:
        draft.walls.length === 0
          ? 'nascem com o ambiente'
          : `${contar(casadas, 'casada', 'casadas')} de ${String(draft.walls.length)}${
              pecas === 0 ? '' : ` · ${contar(pecas, 'peça', 'peças')}`
            }`,
      cheia: draft.walls.length > 0 && casadas > 0,
      disponivel: comEstrutura,
    },
    {
      id: 'etapa-servicos',
      nome: 'Serviços',
      estado:
        servicos === 0
          ? `nenhum escolhido em ${draft.name}`
          : `${contar(servicos, 'escolhido', 'escolhidos')} em ${draft.name}`,
      cheia: servicos > 0,
      disponivel: comEstrutura,
    },
    {
      id: 'etapa-orcamento',
      nome: 'Serviços × traçado',
      estado: servicos === 0 ? 'espera os serviços' : 'medido pelo desenho',
      cheia: servicos > 0,
      disponivel: comEstrutura,
    },
  ];

  return (
    <nav aria-label="Etapas da montagem">
      <ol className="grid grid-cols-2 gap-px overflow-hidden rounded-(--radius-card) border border-(--color-border-subtle) bg-(--color-border-subtle) sm:grid-cols-4 lg:grid-cols-7">
        {etapas.map((etapa, index) => (
          <li key={etapa.id} className="bg-(--color-surface-raised)">
            <a
              href={`#${etapa.id}`}
              aria-disabled={!etapa.disponivel}
              title={etapa.disponivel ? undefined : 'Ligue um pavimento a um desenho primeiro'}
              className={cn(
                'flex h-full flex-col gap-1 px-3 py-2 text-xs transition-colors hover:bg-(--color-surface-muted)',
                !etapa.disponivel && 'pointer-events-none opacity-50',
              )}
            >
              <span className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-5 shrink-0 items-center justify-center rounded-full font-mono text-[11px] font-semibold leading-none',
                    etapa.cheia
                      ? 'bg-(--color-brand) text-(--color-brand-contrast)'
                      : 'border border-(--color-border-subtle) text-(--color-content-muted)',
                  )}
                >
                  {index + 1}
                </span>
                <span className="truncate font-medium text-(--color-content)">{etapa.nome}</span>
              </span>
              <span className="text-(--color-content-muted)">{etapa.estado}</span>
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}
